import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, NavLink, Navigate, Route, Routes, useNavigate, useSearchParams } from "react-router-dom";
import { Activity, AlertTriangle, Archive, ArrowRight, ArrowUp, BookOpen, Bot, CheckCircle2, ChevronDown, Database, FileDown, FileText, Gauge, Home, ListChecks, Lock, Plus, RefreshCw, Search, Settings, ShieldCheck, Sparkles, Trash2, Upload, Wrench, X } from "lucide-react";
import { runAiAssist } from "./ai/gateway";
import { LegalLinks } from "./components/LegalLinks";
import { loadSettings, type PublicSettings } from "./content/yamlLoader";
import { groupedOptions, modelLabel, optionGroups, options, type OptionGroup } from "./domain/options";
import { buildPrivacyPayload } from "./domain/privacy";
import { computeScore, isRealLiteracyEvidence, moduleStatuses } from "./domain/score";
import type { ActionItem, ActionStatus, AITool, AIUseCase, BusinessProfile, GeneratedDocument, Workspace } from "./domain/types";
import { SCHEMA_VERSION } from "./domain/types";
import { deriveRole, deriveTransparency, evaluateUseCaseRisk, now, uid } from "./domain/rules";
import { actionItemsToCsv, buildDossierMarkdown, buildPolicyMarkdown, markdownToPdf, toolsToCsv } from "./export/documentGenerator";
import {
  activateLicenseCode,
  clearActivation,
  completeStripeCheckout,
  disabledLicenseAccess,
  getLicenseQuery,
  licenseErrorMessage,
  lockedLicenseAccess,
  redeemAutoUnlock,
  removeLicenseQueryParams,
  startStripeCheckout,
  validateStoredActivation,
  type GatingConfig,
  type LicenseAccessState
} from "./license/client";
import { clearLocalData, db, downloadFile, getWorkspace, replaceWorkspace } from "./storage/db";

type Snapshot = Workspace | null;
type AppFeatures = {
  aiAssistEnabled: boolean;
  aiAssistUserToggleEnabled: boolean;
  aiAssistUseCaseSummaryEnabled: boolean;
  handbookEnabled: boolean;
};

const disabledAppFeatures: AppFeatures = {
  aiAssistEnabled: false,
  aiAssistUserToggleEnabled: false,
  aiAssistUseCaseSummaryEnabled: false,
  handbookEnabled: false
};

type PolicyConfig = {
  baseVersion: string;
  baseVersionLabel: string;
  documentIdEnabled: boolean;
};

const defaultPolicyConfig: PolicyConfig = {
  baseVersion: "0.1.0",
  baseVersionLabel: "Richtlinien-Version",
  documentIdEnabled: false
};

function policyFromSettings(settings: PublicSettings): PolicyConfig {
  const policy = settings.policy;
  return {
    baseVersion: policy?.baseVersion?.trim() || defaultPolicyConfig.baseVersion,
    baseVersionLabel: policy?.baseVersionLabel?.trim() || defaultPolicyConfig.baseVersionLabel,
    documentIdEnabled: configFlag(policy?.documentIdEnabled, defaultPolicyConfig.documentIdEnabled)
  };
}

const disabledGatingConfig: GatingConfig = {
  enabled: false,
  licenseServerUrl: "",
  manualLicenseCodeEnabled: false,
  stripeCheckoutEnabled: false,
  digistoreMagicLinkEnabled: false,
  launchOfferEnabled: false,
  launchOfferTag: "launch",
  manualCheckoutUrl: "",
  supportUrl: "",
  requestTimeoutMs: 12000
};

function configFlag(value: unknown, fallback = false) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") return ["1", "true", "yes", "on"].includes(value.toLowerCase());
  return fallback;
}

function featuresFromSettings(settings: PublicSettings): AppFeatures {
  return {
    aiAssistEnabled: configFlag(settings.features?.aiAssistEnabled),
    aiAssistUserToggleEnabled: configFlag(settings.features?.aiAssistUserToggleEnabled),
    aiAssistUseCaseSummaryEnabled: configFlag(settings.features?.aiAssistUseCaseSummaryEnabled),
    handbookEnabled: configFlag(settings.features?.handbookEnabled)
  };
}

function gatingFromSettings(settings: PublicSettings): GatingConfig {
  const gating = settings.gating;
  const launchOfferTag = gating?.launchOfferTag === "launch" ? "launch" : "";
  return {
    enabled: configFlag(gating?.enabled),
    licenseServerUrl: gating?.licenseServerUrl ?? "",
    manualLicenseCodeEnabled: configFlag(gating?.manualLicenseCodeEnabled, true),
    stripeCheckoutEnabled: configFlag(gating?.stripeCheckoutEnabled, true),
    digistoreMagicLinkEnabled: configFlag(gating?.digistoreMagicLinkEnabled, true),
    launchOfferEnabled: configFlag(gating?.launchOfferEnabled) && launchOfferTag === "launch",
    launchOfferTag,
    manualCheckoutUrl: gating?.manualCheckoutUrl ?? "",
    supportUrl: gating?.supportUrl ?? "",
    requestTimeoutMs: Number(gating?.requestTimeoutMs ?? 12000)
  };
}

function effectiveFeatures(features: AppFeatures, workspace: Workspace): AppFeatures {
  const localAiAssistEnabled = features.aiAssistUserToggleEnabled
    ? workspace.appSettings[0]?.ai_assist_enabled_by_user === true
    : true;
  return {
    ...features,
    aiAssistEnabled: features.aiAssistEnabled && localAiAssistEnabled
  };
}

function latestByTimestamp<T extends { updated_at?: string; created_at?: string }>(items: T[]) {
  return [...items].sort((a, b) => {
    const left = Date.parse(a.updated_at ?? a.created_at ?? "");
    const right = Date.parse(b.updated_at ?? b.created_at ?? "");
    return (Number.isFinite(right) ? right : 0) - (Number.isFinite(left) ? left : 0);
  })[0];
}

const nav = [
  { to: "/", label: "Dashboard", icon: Home },
  { to: "/profile", label: "Profil / Onboarding", icon: ShieldCheck },
  { to: "/tools", label: "Tool-Register", icon: Wrench },
  { to: "/use-cases", label: "Use Cases", icon: Sparkles },
  { to: "/risk", label: "Risiko-Ampel", icon: AlertTriangle },
  { to: "/actions", label: "Maßnahmen", icon: ListChecks },
  { to: "/role-check", label: "Rollen-Check", icon: ShieldCheck },
  { to: "/literacy", label: "AI-Literacy", icon: CheckCircle2 },
  { to: "/transparency", label: "Transparenz", icon: Lock },
  { to: "/documents", label: "Dokumente", icon: FileText },
  { to: "/export", label: "Export / Backup", icon: FileDown },
  { to: "/handbuch", label: "Handbuch", icon: BookOpen },
  { to: "/settings", label: "Einstellungen", icon: Settings }
];

const moduleRoutes: Record<string, string> = {
  Onboarding: "/profile",
  "Profil / Onboarding": "/profile",
  "Tool-Register": "/tools",
  "Use Cases": "/use-cases",
  "Maßnahmen": "/actions",
  "Rollen-Check": "/role-check",
  "AI-Literacy": "/literacy",
  Transparenz: "/transparency",
  Dokumente: "/documents"
};

export function App() {
  const [searchParams] = useSearchParams();
  const [workspace, setWorkspace] = useState<Snapshot>(null);
  const [features, setFeatures] = useState<AppFeatures>(disabledAppFeatures);
  const [gatingConfig, setGatingConfig] = useState<GatingConfig>(disabledGatingConfig);
  const [policyConfig, setPolicyConfig] = useState<PolicyConfig>(defaultPolicyConfig);
  const [licenseAccess, setLicenseAccess] = useState<LicenseAccessState>({ checking: true, hasAccess: false, mode: "locked" });
  const [loading, setLoading] = useState(true);
  const [workspaceBusy, setWorkspaceBusy] = useState(false);
  const adminMode = searchParams.get("mode") === "admin";
  const adminAiOverride = searchParams.get("adminAi") === "1" && typeof sessionStorage !== "undefined" && Boolean(sessionStorage.getItem("admin-login"));
  async function refresh(nextWorkspace?: Workspace) {
    setWorkspaceBusy(true);
    try {
      const current = nextWorkspace ?? await getWorkspace();
      setWorkspace(await recomputeDerived(current));
    } finally {
      setWorkspaceBusy(false);
    }
  }
  async function refreshLicense(config = gatingConfig, forceRemote = false) {
    setLicenseAccess({ checking: true, hasAccess: false, mode: "locked" });
    setLicenseAccess(await resolveLicenseAccess(config, { forceRemote }));
  }
  useEffect(() => {
    if (!adminMode) {
      let active = true;
      async function boot() {
        setLoading(true);
        const settings = await loadSettings().catch(() => ({} as PublicSettings));
        if (!active) return;
        const nextFeatures = featuresFromSettings(settings);
        const nextGatingConfig = gatingFromSettings(settings);
        setFeatures(nextFeatures);
        setGatingConfig(nextGatingConfig);
        setPolicyConfig(policyFromSettings(settings));
        await refresh();
        const nextAccess = await resolveLicenseAccess(nextGatingConfig);
        if (!active) return;
        setLicenseAccess(nextAccess);
        setLoading(false);
      }
      boot();
      return () => {
        active = false;
      };
    }
  }, [adminMode]);
  if (adminMode) return <AdminPage />;
  if (loading || !workspace) return <LoadingOverlay title="Workspace wird geladen" message="Lokale Datenbank, Einstellungen und Workspace-Daten werden vorbereitet." />;
  if (licenseAccess.checking) return <LoadingOverlay title="Lizenzstatus wird geprüft" message="Die lokale Aktivierung wird gelesen und bei Bedarf validiert." />;
  if (!licenseAccess.hasAccess) {
    return <UnlockPage config={gatingConfig} licenseAccess={licenseAccess} onUnlocked={async () => {
      await refresh();
      await refreshLicense(gatingConfig);
    }} />;
  }
  const onboarded = workspace.businessProfile.some((item) => item.onboarding_completed);
  const profile = workspace.businessProfile[0];
  const visibleFeatures = adminAiOverride
    ? { ...features, aiAssistEnabled: true, aiAssistUserToggleEnabled: false, aiAssistUseCaseSummaryEnabled: true }
    : effectiveFeatures(features, workspace);
  const currentRole = workspace.roleCheckRecords.find((item) => item.id === "role_check_current" && item.ai_act_role_finalized);
   const visibleNav = features.handbookEnabled ? nav : nav.filter((item) => item.to !== "/handbuch");
 return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <ShieldCheck />
          <div><strong>BEN | AI-Act Readiness Kit</strong><span>Solo-Business MVP</span></div>
        </div>
        <nav>{visibleNav.map((item) => <NavLink key={item.to} to={item.to} end={item.to === "/"}><item.icon />{item.label}</NavLink>)}</nav>
        <LegalLinks placement="sidebar" />
      </aside>
      <main>
        <header className="topbar">
          {currentRole?.ai_act_role_profile ? <span className={`preliminary-chip current ${currentRole.ai_act_role_profile === "provider_high_risk" ? "danger" : ""}`}>Aktuell: {modelLabel(currentRole.ai_act_role_profile)} · Deployer {currentRole.ai_act_role_confidence?.deployer ?? 0}% · Provider {currentRole.ai_act_role_confidence?.provider ?? 0}%</span> : profile?.preliminary_role_profile && <span className={`preliminary-chip ${profile.preliminary_role_profile === "provider_high_risk" ? "danger" : ""}`}>Vorab: {modelLabel(profile.preliminary_role_profile)} · Deployer {profile.preliminary_role_confidence?.deployer ?? 0}% · Provider {profile.preliminary_role_confidence?.provider ?? 0}%</span>}
          <span><Database size={16} /> Local-first aktiv · {workspace.appSettings[0]?.storage_mode ?? "indexeddb-fallback"}</span>
          {visibleFeatures.aiAssistEnabled && <span><Bot size={16} /> {workspace.appSettings[0]?.credits_remaining ?? 0} AI-Assists übrig</span>}
          {adminAiOverride && <span><ShieldCheck size={16} /> Admin AI-Assist aktiv</span>}
          {gatingConfig.enabled && licenseAccess.mode === "grace" && <span><AlertTriangle size={16} /> Lizenz-Grace-Period aktiv</span>}
          {!workspace.appSettings[0]?.last_backup_at && <Link to="/export" className="warning-link">Backup empfohlen</Link>}
        </header>
        <Routes>
          <Route path="/onboarding" element={<Onboarding onDone={refresh} />} />
          <Route path="/" element={onboarded ? <Dashboard workspace={workspace} features={visibleFeatures} onRefresh={refresh} /> : <Navigate to="/onboarding" />} />
          <Route path="/profile" element={<Onboarding onDone={refresh} edit />} />
          <Route path="/tools" element={<Tools workspace={workspace} onRefresh={refresh} />} />
          <Route path="/use-cases" element={<UseCases workspace={workspace} features={visibleFeatures} onRefresh={refresh} />} />
          <Route path="/risk" element={<Risk workspace={workspace} />} />
          <Route path="/actions" element={<Actions workspace={workspace} onRefresh={refresh} />} />
          <Route path="/role-check" element={<RoleCheck workspace={workspace} features={visibleFeatures} onRefresh={refresh} />} />
          <Route path="/literacy" element={<Literacy workspace={workspace} onRefresh={refresh} />} />
          <Route path="/transparency" element={<Transparency workspace={workspace} features={visibleFeatures} onRefresh={refresh} />} />
          <Route path="/documents" element={<Documents workspace={workspace} features={visibleFeatures} policyConfig={policyConfig} onRefresh={refresh} />} />
          <Route path="/export" element={<ExportImport workspace={workspace} features={visibleFeatures} policyConfig={policyConfig} onRefresh={refresh} />} />
          <Route path="/import" element={<ExportImport workspace={workspace} features={visibleFeatures} policyConfig={policyConfig} onRefresh={refresh} />} />
          <Route path="/handbuch" element={features.handbookEnabled ? <HandbookPage /> : <Navigate to="/" />} />
          <Route path="/settings" element={<SettingsPage workspace={workspace} features={features} gatingConfig={gatingConfig} licenseAccess={licenseAccess} onLicenseRefresh={() => refreshLicense(gatingConfig, true)} onRefresh={refresh} />} />
        </Routes>
        {workspaceBusy && !loading && <LoadingOverlay title="Änderungen werden übernommen" message="Workspace-Daten werden gespeichert, neu berechnet und lokal aktualisiert." />}
      </main>
    </div>
  );
}

function LoadingOverlay({ title, message }: { title: string; message: string }) {
  return (
    <div className="loading-overlay" role="status" aria-live="polite" aria-busy="true">
      <div className="loading-panel">
        <div className="loading-spinner" aria-hidden="true" />
        <div className="loading-copy">
          <strong>{title}</strong>
          <span>{message}</span>
        </div>
        <div className="loading-progress" aria-hidden="true"><span /></div>
      </div>
    </div>
  );
}
async function resolveLicenseAccess(config: GatingConfig, options: { forceRemote?: boolean } = {}): Promise<LicenseAccessState> {
  const query = getLicenseQuery(window.location.search);
  if (!config.enabled) {
    if (query.autoUnlockToken || query.checkout || query.sessionId) removeLicenseQueryParams();
    return disabledLicenseAccess;
  }
  if (query.autoUnlockToken && !config.digistoreMagicLinkEnabled) {
    removeLicenseQueryParams();
    return validateStoredActivation(config, options);
  }
  if (query.autoUnlockToken && config.digistoreMagicLinkEnabled) {
    try {
      const activation = await redeemAutoUnlock(config, query.autoUnlockToken);
      removeLicenseQueryParams();
      return { checking: false, hasAccess: true, mode: "licensed", activation, message: "App wurde automatisch freigeschaltet." };
    } catch (error) {
      removeLicenseQueryParams();
      return { ...lockedLicenseAccess, message: licenseErrorMessage(error) };
    }
  }
  if (query.checkout && !config.stripeCheckoutEnabled) {
    removeLicenseQueryParams();
    return validateStoredActivation(config, options);
  }
  if (query.checkout === "success" && query.sessionId) {
    try {
      const activation = await completeStripeCheckout(config, query.sessionId);
      removeLicenseQueryParams();
      return { checking: false, hasAccess: true, mode: "licensed", activation, message: "Stripe Checkout bestaetigt. App ist freigeschaltet." };
    } catch (error) {
      removeLicenseQueryParams();
      return { ...lockedLicenseAccess, message: licenseErrorMessage(error) };
    }
  }
  if (query.checkout === "cancelled") {
    removeLicenseQueryParams();
    return { ...lockedLicenseAccess, message: "Checkout wurde abgebrochen. Du kannst die App jederzeit freischalten." };
  }
  return validateStoredActivation(config, options);
}

async function recomputeDerived(workspace: Workspace): Promise<Workspace> {
  const activeUseCases = workspace.aiUseCases.filter((item) => item.status !== "archived");
  const activeTools = workspace.aiTools.filter((item) => item.status !== "archived");
  const risks = activeUseCases.map(evaluateUseCaseRisk);
  const existingRole = workspace.roleCheckRecords.find((item) => item.id === "role_check_current");
  const currentAiActRole = existingRole?.ai_act_role_finalized ? deriveCurrentAiActRole(workspace.businessProfile[0], activeUseCases) : undefined;
  const latestRole = workspace.businessProfile.length || activeUseCases.length ? {
    ...deriveRole(workspace.businessProfile[0], activeUseCases),
    id: "role_check_current",
    ai_act_role_profile: currentAiActRole?.profile,
    ai_act_role_confidence: currentAiActRole?.confidence,
    ai_act_role_finalized: existingRole?.ai_act_role_finalized
  } : undefined;
  const roleCheckRecords = latestRole ? [latestRole] : [];
  const score = computeScore({ profiles: workspace.businessProfile, tools: activeTools, useCases: activeUseCases, risks, roles: roleCheckRecords, literacy: workspace.literacyRecords, transparency: workspace.transparencyChecks, documents: workspace.generatedDocuments, actionItems: workspace.actionItems });
  const nextModuleStatuses = moduleStatuses({ profiles: workspace.businessProfile, tools: activeTools, useCases: activeUseCases, roles: roleCheckRecords, literacy: workspace.literacyRecords, transparency: workspace.transparencyChecks, documents: workspace.generatedDocuments, actionItems: workspace.actionItems });

  await db.transaction("rw", [db.riskAssessments, db.roleCheckRecords, db.readinessScores, db.moduleStatuses], async () => {
    await replaceTableIfChanged(db.riskAssessments, workspace.riskAssessments, risks);
    await replaceTableIfChanged(db.roleCheckRecords, workspace.roleCheckRecords, roleCheckRecords);
    await replaceTableIfChanged(db.readinessScores, workspace.readinessScores, [score]);
    await replaceTableIfChanged(db.moduleStatuses, workspace.moduleStatuses, nextModuleStatuses);
  });

  return {
    ...workspace,
    riskAssessments: risks,
    roleCheckRecords,
    readinessScores: [score],
    moduleStatuses: nextModuleStatuses
  };
}

async function replaceTableIfChanged<T extends { id: string }>(table: { clear: () => Promise<void>; bulkPut: (records: T[]) => Promise<void> }, current: T[], next: T[]) {
  if (sameRecords(current, next)) return;
  await table.clear();
  if (next.length) await table.bulkPut(next);
}

function sameRecords<T extends { id: string }>(left: T[], right: T[]) {
  if (left.length !== right.length) return false;
  const normalize = (items: T[]) => [...items]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((item) => JSON.stringify(withoutVolatileTimestamps(item)));
  const leftJson = normalize(left);
  const rightJson = normalize(right);
  return leftJson.every((value, index) => value === rightJson[index]);
}

function replaceRecord<T extends { id: string }>(records: T[], next: T) {
  return records.some((record) => record.id === next.id)
    ? records.map((record) => record.id === next.id ? next : record)
    : [...records, next];
}

function removeRecord<T extends { id: string }>(records: T[], id: string) {
  return records.filter((record) => record.id !== id);
}
function withoutVolatileTimestamps<T extends { id: string }>(item: T) {
  const { created_at: _createdAt, updated_at: _updatedAt, ...stable } = item as T & { created_at?: string; updated_at?: string };
  return stable;
}
function Page({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return <section className="page"><div className="page-title"><h1>{title}</h1>{action}</div>{children}</section>;
}

function UnlockPage({ config, licenseAccess, onUnlocked }: { config: GatingConfig; licenseAccess: LicenseAccessState; onUnlocked: () => Promise<void> }) {
  const [licenseCode, setLicenseCode] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState<"license" | "stripe" | null>(null);
  const [message, setMessage] = useState(licenseAccess.message ?? "");

  const [checkoutProgress, setCheckoutProgress] = useState(0);
  useEffect(() => {
    if (busy !== "stripe") {
      setCheckoutProgress(0);
      return;
    }
    setCheckoutProgress(12);
    const interval = window.setInterval(() => {
      setCheckoutProgress((current) => Math.min(92, current + Math.max(2, Math.round((92 - current) * 0.16))));
    }, 420);
    return () => window.clearInterval(interval);
  }, [busy]);
  async function activate(event: FormEvent) {
    event.preventDefault();
    if (!licenseCode.trim()) return;
    setBusy("license");
    setMessage("");
    try {
      await activateLicenseCode(config, licenseCode.trim());
      await onUnlocked();
    } catch (error) {
      setMessage(licenseErrorMessage(error));
    } finally {
      setBusy(null);
    }
  }

  async function checkout() {
    setBusy("stripe");
    setMessage("");
    try {
      await startStripeCheckout(config, email.trim());
    } catch (error) {
      setMessage(licenseErrorMessage(error));
      setBusy(null);
    }
  }

  return (
    <main className="unlock-shell">
      <section className="unlock-panel">
        <div className="brand unlock-brand"><ShieldCheck /><div><strong>BEN | AI-Act Readiness Kit</strong><span>Freischaltung</span></div></div>
        <div className="unlock-card">
          <Lock />
          <div>
            <h1>App freischalten</h1>
            <p>Diese Installation benötigt eine gültige Lizenz. Die Workspace-Daten bleiben lokal in deinem Browser.</p>
          </div>
        </div>
        {message && <div className="warning-card"><AlertTriangle /> {message}</div>}
        {!config.licenseServerUrl && <div className="warning-card"><AlertTriangle /> In <code>settings.yaml</code> ist noch keine <code>gating.licenseServerUrl</code> gesetzt.</div>}
        {config.manualLicenseCodeEnabled && <form className="form-panel unlock-form" onSubmit={activate}>
          <Label label="Lizenzcode">
            <input value={licenseCode} onChange={(event) => setLicenseCode(event.target.value)} placeholder="AIK-XXXX-XXXX-XXXX" autoComplete="off" />
          </Label>
          <button className="button primary" type="submit" disabled={busy !== null || !licenseCode.trim() || !config.licenseServerUrl}>
            <Lock /> {busy === "license" ? "Aktiviere..." : "Lizenz aktivieren"}
          </button>
        </form>}
        {config.stripeCheckoutEnabled && <div className="form-panel unlock-form">
          <Label label="E-Mail für Stripe Checkout (optional)">
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@example.com" />
          </Label>
          <button className={`button secondary checkout-button ${busy === "stripe" ? "is-loading" : ""}`} onClick={checkout} disabled={busy !== null || !config.licenseServerUrl} aria-busy={busy === "stripe"}>
            {busy === "stripe" ? <span className="checkout-spinner" aria-hidden="true" /> : <ArrowRight />}
            <span>{busy === "stripe" ? "Stripe Checkout wird vorbereitet..." : "Per Stripe kaufen"}</span>
            {busy === "stripe" && <span className="checkout-button-progress" style={{ width: `${checkoutProgress}%` }} aria-hidden="true" />}
          </button>
          {busy === "stripe" && <div className="checkout-progress-card" role="status" aria-live="polite">
            <div className="checkout-progress-heading"><span className="checkout-spinner" aria-hidden="true" /><strong>Checkout wird gestartet</strong><span>{checkoutProgress}%</span></div>
            <div className="checkout-progress-track"><span style={{ width: `${checkoutProgress}%` }} /></div>
            <p>Bitte diese Seite offen lassen. Du wirst gleich sicher zu Stripe weitergeleitet.</p>
          </div>}
        </div>}
        <div className="unlock-actions">
          {config.manualCheckoutUrl && <a className="button ghost" href={config.manualCheckoutUrl} target="_blank" rel="noreferrer">Weitere Kaufoptionen</a>}
          {config.supportUrl && <a className="button ghost" href={config.supportUrl} target="_blank" rel="noreferrer">Support</a>}
        </div>
        <LegalLinks placement="unlock" />
      </section>
    </main>
  );
}

type AdminLogEntry = {
  id: string;
  started_at: string;
  finished_at?: string;
  duration_ms?: number;
  status: "running" | "success" | "error";
  request: unknown;
  validation?: unknown;
  provider?: unknown;
  response?: unknown;
  error?: string;
};

type AdminStateResponse = {
  ok: boolean;
  loggingEnabled: boolean;
  hardDisabled?: boolean;
  adminOnly?: boolean;
  logs: AdminLogEntry[];
  retention?: { mode: string; max_entries: number; note: string };
  error?: string;
};

function AdminPage() {
  const [loginInput, setLoginInput] = useState(() => sessionStorage.getItem("admin-login") ?? "");
  const [login, setLogin] = useState(() => sessionStorage.getItem("admin-login") ?? "");
  const [adminState, setAdminState] = useState<AdminStateResponse | null>(null);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const selectedLog = adminState?.logs.find((log) => log.id === selectedLogId) ?? adminState?.logs[0];

  async function callAdmin(action: string, extra: Record<string, unknown> = {}, loginOverride = login) {
    setBusy(true);
    setError("");
    try {
      const response = await fetch("/api/ai-assist?admin=1", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, login: loginOverride, ...extra })
      });
      const data = await response.json() as AdminStateResponse;
      if (!response.ok || !data.ok) throw new Error(data.error ?? "Admin Request fehlgeschlagen.");
      setAdminState(data);
      if (!selectedLogId && data.logs[0]) setSelectedLogId(data.logs[0].id);
      return data;
    } catch (adminError) {
      setError(adminError instanceof Error ? adminError.message : "Admin Request fehlgeschlagen.");
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function unlock(event: FormEvent) {
    event.preventDefault();
    const data = await callAdmin("status", {}, loginInput);
    if (!data) return;
    sessionStorage.setItem("admin-login", loginInput);
    setLogin(loginInput);
  }

  async function toggleLogging() {
    await callAdmin("setLogging", { enabled: !adminState?.loggingEnabled });
  }

  async function clearLogs() {
    await callAdmin("clearLogs");
    setSelectedLogId(null);
  }

  useEffect(() => {
    if (login) callAdmin("status");
  }, []);

  if (!adminState) {
    return (
      <main className="admin-shell">
        <section className="admin-login-panel">
          <div className="brand admin-brand"><ShieldCheck /><div><strong>AI-Assist Admin</strong><span>Hidden Debug Console</span></div></div>
          <form onSubmit={unlock} className="form-panel">
            <label className="field">
              <span>ADMIN_LOGIN</span>
              <input type="password" value={loginInput} onChange={(event) => setLoginInput(event.target.value)} autoFocus />
            </label>
            {error && <div className="warning-card"><AlertTriangle /> {error}</div>}
            <button className="button primary" type="submit" disabled={busy || !loginInput}><Lock /> Entsperren</button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-shell">
      <section className="admin-page">
        <div className="page-title">
          <div>
            <h1>AI-Assist Admin</h1>
            <p>Function Calls, Provider Payloads und Responses fuer Debugging.</p>
          </div>
          <div className="page-actions">
            <button className={`button ${adminState.loggingEnabled ? "primary" : "secondary"}`} onClick={toggleLogging} disabled={busy}>
              <Activity /> Logging {adminState.loggingEnabled ? "aktiv" : "inaktiv"}
            </button>
            <button className="button secondary" onClick={() => callAdmin("status")} disabled={busy}><RefreshCw /> Aktualisieren</button>
            <Link className="button secondary" to="/?adminAi=1"><Bot /> Admin App</Link>
            <button className="button danger" onClick={clearLogs} disabled={busy || !adminState.logs.length}><Trash2 /> Logs leeren</button>
          </div>
        </div>

        {error && <div className="warning-card"><AlertTriangle /> {error}</div>}
        {adminState.hardDisabled && <div className="warning-card"><AlertTriangle /> AI-Assist Hard-Switch aktiv: OpenAI-Requests sind serverseitig blockiert.</div>}
        {adminState.adminOnly && <div className="admin-note">AI-Assist API ist auf Admin-Requests beschränkt.</div>}
        <div className="admin-note">{adminState.retention?.note ?? "Logs werden in-memory gehalten."}</div>
        <div className="admin-grid">
          <aside className="admin-log-list">
            <h2>Calls ({adminState.logs.length})</h2>
            {adminState.logs.length === 0 && <div className="empty-state">Noch keine AI-Assist Calls geloggt. Logging aktivieren und AI-Assist erneut starten.</div>}
            {adminState.logs.map((log) => (
              <button key={log.id} className={`admin-log-item ${selectedLog?.id === log.id ? "active" : ""}`} onClick={() => setSelectedLogId(log.id)}>
                <span className={`status ${log.status === "success" ? "complete" : log.status === "error" ? "needs_review" : "in_progress"}`}>{log.status}</span>
                <strong>{new Date(log.started_at).toLocaleString("de-DE")}</strong>
                <small>{log.duration_ms ?? 0} ms</small>
              </button>
            ))}
          </aside>
          <section className="admin-log-detail">
            {selectedLog ? (
              <>
                <div className="admin-detail-header">
                  <h2>{selectedLog.id}</h2>
                  <span className={`status ${selectedLog.status === "success" ? "complete" : selectedLog.status === "error" ? "needs_review" : "in_progress"}`}>{selectedLog.status}</span>
                </div>
                <AdminJsonBlock title="Incoming Request" value={selectedLog.request} />
                <AdminJsonBlock title="Validation" value={selectedLog.validation} />
                <AdminJsonBlock title="Provider Call" value={selectedLog.provider} />
                <AdminJsonBlock title="Function Response" value={selectedLog.response} />
                {selectedLog.error && <AdminJsonBlock title="Error" value={selectedLog.error} />}
              </>
            ) : (
              <div className="empty-state">Kein Log ausgewaehlt.</div>
            )}
          </section>
        </div>
      </section>
    </main>
  );
}

function AdminJsonBlock({ title, value }: { title: string; value: unknown }) {
  return (
    <details className="admin-json-block" open>
      <summary>{title}</summary>
      <pre>{JSON.stringify(value ?? null, null, 2)}</pre>
    </details>
  );
}

function Dashboard({ workspace, features, onRefresh }: { workspace: Workspace; features: AppFeatures; onRefresh: () => Promise<void> }) {
  const score = latestByTimestamp(workspace.readinessScores);
  const red = workspace.riskAssessments.filter((item) => item.traffic_light === "red");
  const activeTools = workspace.aiTools.filter((item) => item.status !== "archived");
  const activeUseCases = workspace.aiUseCases.filter((item) => item.status !== "archived");
  const latestDashboardAssist = latestByTimestamp(workspace.generatedDocuments.filter((doc) => doc.type === "dashboard_next_steps"));
  return (
    <Page title="Dashboard" action={<div className="page-actions"><Link className="button secondary" to="/tools"><Plus size={16} /> KI-Tool erfassen</Link><Link className="button primary" to="/use-cases"><Plus size={16} /> Use Case erfassen</Link></div>}>
      <div className="grid">
        <div className="score-card"><Gauge /><strong>{score?.score ?? 0}/100</strong><span>{score?.explanation}</span></div>
        <InfoCard title="Tools" value={activeTools.length} to="/tools" />
        <InfoCard title="Use Cases" value={activeUseCases.length} to="/use-cases" />
        <InfoCard title="Rote Prüfpunkte" value={red.length} to="/risk" tone={red.length ? "danger" : "ok"} />
      </div>
      {red.length > 0 && <div className="warning-card"><AlertTriangle /> Rote Prüfpunkte bleiben sichtbar und erfordern externe Prüfung. Ein hoher Score löst sie nicht auf.</div>}
      <h2>Module</h2>
      <div className="cards">
        {workspace.moduleStatuses.map((item) => (
          <Link className="card module-card" to={moduleRoutes[item.module] ?? "/"} key={item.id} aria-label={`${item.module} öffnen`}>
            <Status state={item.state} />
            <strong>{item.module}</strong>
            <p>{item.summary}</p>
          </Link>
        ))}
      </div>
      {features.aiAssistEnabled && <><div className="dashboard-assist-action">
        <AiAssistButton workspace={workspace} task="dashboard_next_steps" module="Dashboard" onDone={onRefresh} />
      </div>
      <AiAssistResult title="Empfohlene nächste Schritte" document={latestDashboardAssist} emptyText="Noch keine AI-Assist-Empfehlung gespeichert. Starte den AI-Assist, um lokale nächste Schritte zu erzeugen." /></>}
    </Page>
  );
}

function InfoCard({ title, value, to, tone }: { title: string; value: number; to: string; tone?: string }) {
  return <Link to={to} className={`metric ${tone ?? ""}`}><span>{title}</span><strong>{value}</strong></Link>;
}

function needsProfileCustomText(value: string) {
  return value === "other" || value === "unknown";
}

function normalizeCustomerType(value?: string) {
  if (value === "clients") return "mostly_b2b";
  return value ?? options.customerTypes[0]?.value ?? "mostly_b2b";
}

const preliminaryAssessmentQuestions: Array<{ id: string; label: string }> = [
  { id: "uses_third_party_ai", label: "Ich nutze überwiegend fertige KI-Tools als Anwender:in im eigenen Arbeitsprozess." },
  { id: "offers_ai_to_clients", label: "Ich stelle Kunden ein KI-System, einen KI-Workflow oder ein KI-gestütztes Produkt bereit." },
  { id: "own_brand_or_configuration", label: "Ich entwickle, konfiguriere oder vermarkte KI-Funktionen unter eigenem Namen oder als eigener Servicebestandteil." },
  { id: "people_decisions", label: "Die KI kann Entscheidungen oder Empfehlungen über Personen beeinflussen, z. B. Bewerbung, Bewertung, Zugang, Kredit, Bildung, Gesundheit." },
  { id: "high_risk_domain", label: "Der Einsatz berührt hochrisikonahe Bereiche wie Beschäftigung, Bildung, Kreditwürdigkeit, Gesundheits-/psychologische Diagnostik, biometrische Analyse, kritische Infrastruktur oder öffentliche Sicherheit." }
];

function derivePreliminaryAssessment(answers: Record<string, string>) {
  const highRisk = answers.people_decisions === "yes" || answers.high_risk_domain === "yes";
  const providerSignal = answers.offers_ai_to_clients === "yes" || answers.own_brand_or_configuration === "yes";
  const deployerSignal = answers.uses_third_party_ai === "yes";
  const profile: NonNullable<BusinessProfile["preliminary_role_profile"]> = highRisk ? "provider_high_risk" : providerSignal ? "provider_non_high_risk" : "deployer";
  const confidence = highRisk
    ? { deployer: deployerSignal ? 20 : 10, provider: providerSignal ? 90 : 80 }
    : providerSignal
      ? { deployer: deployerSignal ? 35 : 20, provider: deployerSignal ? 65 : 80 }
      : { deployer: deployerSignal ? 85 : 65, provider: deployerSignal ? 15 : 35 };
  return { profile, confidence };
}

function deriveCurrentAiActRole(profile: BusinessProfile | undefined, useCases: AIUseCase[]) {
  const preliminary = profile?.preliminary_role_profile
    ? { profile: profile.preliminary_role_profile, confidence: profile.preliminary_role_confidence ?? { deployer: 50, provider: 50 } }
    : derivePreliminaryAssessment({});
  const hasHighRiskSignal = useCases.some((item) =>
    item.automated_decision_about_people === "yes"
    || item.data_categories.some((category) => ["applicant_employee_data", "health_coaching_information", "health_or_psychological_diagnostics", "applicant_or_employee_evaluation", "scoring_or_prioritization_of_people", "financial_credit_evaluation", "biometric_or_emotional_analysis", "minors"].includes(category))
  );
  const hasProviderSignal = useCases.some((item) =>
    item.context === "digital_product"
    || item.context === "client_workflow_handover"
    || item.data_categories.includes("ai_system_provided_to_clients")
    || item.customer_interaction === "yes"
  );
  const profileResult: NonNullable<BusinessProfile["preliminary_role_profile"]> = hasHighRiskSignal ? "provider_high_risk" : hasProviderSignal || preliminary.profile !== "deployer" ? "provider_non_high_risk" : "deployer";
  const confidence = profileResult === "provider_high_risk"
    ? { deployer: 10, provider: 90 }
    : profileResult === "provider_non_high_risk"
      ? { deployer: hasProviderSignal ? 25 : preliminary.confidence.deployer, provider: hasProviderSignal ? 75 : preliminary.confidence.provider }
      : { deployer: Math.max(preliminary.confidence.deployer, 80), provider: Math.min(preliminary.confidence.provider, 20) };
  return { profile: profileResult, confidence };
}

function preliminaryAssessmentLabel(profile?: string) {
  return profile ? modelLabel(profile) : "Vorabprofil offen";
}

function businessProfileEvidenceLines(profile?: BusinessProfile) {
  if (!profile) return ["Business: nicht angegeben"];
  const withCustom = (value: string, custom?: string) => custom?.trim() ? `${modelLabel(value)} (${custom.trim()})` : modelLabel(value);
  return [
    `Business: ${profile.business_name || "nicht angegeben"}`,
    `Inhaber:in / Verantwortliche Person: ${profile.owner_name || "nicht angegeben"}`,
    `Dokumentation für: ${profile.documentation_recipient || "nicht angegeben"}`,
    `Ort / Land: ${profile.business_location || "nicht angegeben"}`,
    `Business-Typ: ${withCustom(profile.business_segment, profile.business_segment_custom)}`,
    `Leistungsbereich: ${profile.service_areas.map((area) => withCustom(area, profile.service_area_custom)).join(", ") || "nicht angegeben"}`,
    `Kundenart: ${profile.customer_types.map((type) => modelLabel(normalizeCustomerType(type))).join(", ") || "nicht angegeben"}`,
    `KI-Nutzungsgrad: ${withCustom(profile.ai_usage_level, profile.ai_usage_custom)}`,
    `Vorabprofil: ${preliminaryAssessmentLabel(profile.preliminary_role_profile)}`
  ];
}

function AiAssistResult({ title, document, emptyText }: { title: string; document?: GeneratedDocument; emptyText: string }) {
  if (!document) {
    return <div className="assist-result empty-state">{emptyText}</div>;
  }
  return (
    <section className="assist-result">
      <div className="section-heading">
        <h2>{title}</h2>
        <span>{new Date(document.created_at).toLocaleDateString("de-DE")}</span>
      </div>
      <pre>{document.content_markdown}</pre>
    </section>
  );
}

function DocumentPreview({ markdown, type }: { markdown: string; type?: "dossier" | "default" }) {
  if (type !== "dossier") return <div className="document-preview"><pre>{markdown}</pre></div>;
  const [firstLine = "", ...rest] = markdown.split("\n");
  const match = firstLine.match(/^(# AI-Act Readiness Summary)\s+(Kit .*)$/);
  if (!match) return <div className="document-preview"><pre>{markdown}</pre></div>;
  return (
    <div className="document-preview">
      <div className="document-title-row">
        <span>{match[1]}</span>
        <span>{match[2]}</span>
      </div>
      <pre>{rest.join("\n")}</pre>
    </div>
  );
}

function exportDateStamp(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function datedFilename(base: string, extension: string) {
  return `${base}_${exportDateStamp()}.${extension}`;
}

const crcTable = Array.from({ length: 256 }, (_, index) => {
  let value = index;
  for (let bit = 0; bit < 8; bit += 1) value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
  return value >>> 0;
});

function crc32(bytes: Uint8Array) {
  let crc = 0xffffffff;
  bytes.forEach((byte) => {
    crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  });
  return (crc ^ 0xffffffff) >>> 0;
}

function dosDateTime(date = new Date()) {
  const time = (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
  const dosDate = ((date.getFullYear() - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();
  return { time, date: dosDate };
}

async function zipFiles(files: Array<{ name: string; content: string | Blob }>) {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  const stamp = dosDateTime();

  for (const file of files) {
    const nameBytes = encoder.encode(file.name);
    const data = file.content instanceof Blob ? new Uint8Array(await file.content.arrayBuffer()) : encoder.encode(file.content);
    const crc = crc32(data);
    const local = new Uint8Array(30 + nameBytes.length);
    const view = new DataView(local.buffer);
    view.setUint32(0, 0x04034b50, true);
    view.setUint16(4, 20, true);
    view.setUint16(6, 0x0800, true);
    view.setUint16(10, stamp.time, true);
    view.setUint16(12, stamp.date, true);
    view.setUint32(14, crc, true);
    view.setUint32(18, data.length, true);
    view.setUint32(22, data.length, true);
    view.setUint16(26, nameBytes.length, true);
    local.set(nameBytes, 30);
    chunks.push(local, data);

    const header = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(header.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint16(8, 0x0800, true);
    centralView.setUint16(12, stamp.time, true);
    centralView.setUint16(14, stamp.date, true);
    centralView.setUint32(16, crc, true);
    centralView.setUint32(20, data.length, true);
    centralView.setUint32(24, data.length, true);
    centralView.setUint16(28, nameBytes.length, true);
    centralView.setUint32(42, offset, true);
    header.set(nameBytes, 46);
    central.push(header);
    offset += local.length + data.length;
  }

  const centralOffset = offset;
  const centralSize = central.reduce((sum, item) => sum + item.length, 0);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(8, files.length, true);
  endView.setUint16(10, files.length, true);
  endView.setUint32(12, centralSize, true);
  endView.setUint32(16, centralOffset, true);
  const blobParts = [...chunks, ...central, end].map((part) => {
    const copy = new ArrayBuffer(part.byteLength);
    new Uint8Array(copy).set(part);
    return copy;
  });
  return new Blob(blobParts, { type: "application/zip" });
}

function Onboarding({ onDone, edit }: { onDone: () => Promise<void>; edit?: boolean }) {
  const navigate = useNavigate();
  const [businessName, setBusinessName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [documentationRecipient, setDocumentationRecipient] = useState("");
  const [businessLocation, setBusinessLocation] = useState("");
  const [businessSegment, setBusinessSegment] = useState(options.businessTypes[0]?.value ?? "freelancer");
  const [businessSegmentCustom, setBusinessSegmentCustom] = useState("");
  const [serviceAreas, setServiceAreas] = useState(options.serviceAreas[0]?.value ?? "consulting");
  const [serviceAreaCustom, setServiceAreaCustom] = useState("");
  const [customerTypes, setCustomerTypes] = useState(options.customerTypes[0]?.value ?? "mostly_b2b");
  const [customerTypeCustom, setCustomerTypeCustom] = useState("");
  const [aiUsage, setAiUsage] = useState(options.aiUsageMaturity[0]?.value ?? "testing_occasionally");
  const [aiUsageCustom, setAiUsageCustom] = useState("");
  const [preliminaryAnswers, setPreliminaryAnswers] = useState<Record<string, string>>({
    uses_third_party_ai: "yes",
    offers_ai_to_clients: "no",
    own_brand_or_configuration: "no",
    people_decisions: "no",
    high_risk_domain: "no"
  });
  const [highRiskDialogOpen, setHighRiskDialogOpen] = useState(false);
  useEffect(() => {
    db.businessProfile.toArray().then(([existing]) => {
      if (!existing) return;
      setBusinessName(existing.business_name ?? "");
      setOwnerName(existing.owner_name ?? "");
      setDocumentationRecipient(existing.documentation_recipient ?? "");
      setBusinessLocation(existing.business_location ?? "");
      setBusinessSegment(existing.business_segment);
      setBusinessSegmentCustom(existing.business_segment_custom ?? "");
      setServiceAreas(existing.service_areas[0] ?? options.serviceAreas[0]?.value ?? "consulting");
      setServiceAreaCustom(existing.service_area_custom ?? "");
      setCustomerTypes(normalizeCustomerType(existing.customer_types[0]));
      setCustomerTypeCustom(existing.customer_type_custom ?? "");
      setAiUsage(existing.ai_usage_level);
      setAiUsageCustom(existing.ai_usage_custom ?? "");
      setPreliminaryAnswers((current) => ({ ...current, ...(existing.preliminary_role_answers ?? {}) }));
    });
  }, []);
  const preliminaryAssessment = derivePreliminaryAssessment(preliminaryAnswers);
  async function save() {
    if (preliminaryAssessment.profile === "provider_high_risk" && !highRiskDialogOpen) {
      setHighRiskDialogOpen(true);
      return;
    }
    const existing = (await db.businessProfile.toArray())[0];
    const record: BusinessProfile = {
      id: existing?.id ?? uid("profile"),
      schema_version: SCHEMA_VERSION,
      created_at: existing?.created_at ?? now(),
      updated_at: now(),
      business_name: businessName.trim() || undefined,
      owner_name: ownerName.trim() || undefined,
      documentation_recipient: documentationRecipient.trim() || undefined,
      business_location: businessLocation.trim() || undefined,
      business_segment: businessSegment,
      business_segment_custom: businessSegmentCustom.trim() || undefined,
      service_areas: [serviceAreas],
      service_area_custom: serviceAreaCustom.trim() || undefined,
      customer_types: [normalizeCustomerType(customerTypes)],
      customer_type_custom: customerTypeCustom.trim() || undefined,
      ai_usage_level: aiUsage,
      ai_usage_custom: aiUsageCustom.trim() || undefined,
      handles_sensitive_contexts: "unknown",
      preliminary_role_profile: preliminaryAssessment.profile,
      preliminary_role_confidence: preliminaryAssessment.confidence,
      preliminary_role_answers: preliminaryAnswers as BusinessProfile["preliminary_role_answers"],
      onboarding_completed: true,
      local_first_acknowledged: true
    };
    await db.businessProfile.put(record);
    await onDone();
    navigate("/");
  }
  return (
    <Page title={edit ? "Business-Profil" : "Onboarding"}>
      <div className="form-panel">
        <div className="form-row">
          <Label label="Business-Name"><input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="z. B. Muster Studio" /></Label>
          <Label label="Inhaber:in / Verantwortliche Person"><input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="z. B. Max Mustermann" /></Label>
        </div>
        <div className="form-row">
          <Label label="Dokumentation für"><input value={documentationRecipient} onChange={(e) => setDocumentationRecipient(e.target.value)} placeholder="z. B. interne Workspace Summary" /></Label>
          <Label label="Ort / Land (optional)"><input value={businessLocation} onChange={(e) => setBusinessLocation(e.target.value)} placeholder="z. B. Berlin, Deutschland" /></Label>
        </div>
        <p className="privacy-note">Wichtiger Hinweis: Local-first bedeutet deine Readiness-Daten bleiben nur in deinem Browser - keine externen Server, keine Cloud. Erstelle deshalb regelmäßig ein JSON-Backup (-&gt; Export / Backup). Du kannst dann dieses Backup auf einem anderen Gerät oder einem anderem Browser durch Importieren wiederherstellen.</p>
        <section className={`pre-assessment ${preliminaryAssessment.profile === "provider_high_risk" ? "danger" : ""}`}>
          <div className="section-heading">
            <div>
              <h2>Vorab-Assessment</h2>
              <p>Maximal fünf Fragen für eine erste Einordnung als Deployer oder Provider. Das Ergebnis ist vorläufig und kann sich durch spätere Use Cases ändern.</p>
            </div>
            <span className="record-id">{modelLabel(preliminaryAssessment.profile)}</span>
          </div>
          <div className="pre-assessment-result">
            <strong>Vorläufig: {modelLabel(preliminaryAssessment.profile)}</strong>
            <span>Wahrscheinlichkeit Deployer {preliminaryAssessment.confidence.deployer}% · Provider {preliminaryAssessment.confidence.provider}%</span>
          </div>
          <div className="pre-question-list">
            {preliminaryAssessmentQuestions.map((question) => (
              <label className="pre-question" key={question.id}>
                <span>{question.label}</span>
                <select value={preliminaryAnswers[question.id] ?? "unknown"} onChange={(event) => setPreliminaryAnswers((current) => ({ ...current, [question.id]: event.target.value }))}>
                  <option value="yes">Ja</option>
                  <option value="no">Nein</option>
                  <option value="unknown">Unsicher</option>
                </select>
              </label>
            ))}
          </div>
          {preliminaryAssessment.profile === "provider_high_risk" && <p className="danger-text">Hochrisiko-Signal erkannt: Dieses Kit kann dann nur eine Basishilfe sein. Es ersetzt keine externe Prüfung und keine umfassenden organisatorischen, technischen und rechtlichen Maßnahmen.</p>}
        </section>        <Label label="Business-Typ"><select value={businessSegment} onChange={(e) => setBusinessSegment(e.target.value)}><OptionList items={options.businessTypes} groups={optionGroups.businessTypes} /></select>{needsProfileCustomText(businessSegment) && <input value={businessSegmentCustom} onChange={(e) => setBusinessSegmentCustom(e.target.value)} placeholder="Bitte kurz beschreiben" />}</Label>
        <Label label="Leistungsbereich"><select value={serviceAreas} onChange={(e) => setServiceAreas(e.target.value)}><OptionList items={options.serviceAreas} groups={optionGroups.serviceAreas} /></select>{needsProfileCustomText(serviceAreas) && <input value={serviceAreaCustom} onChange={(e) => setServiceAreaCustom(e.target.value)} placeholder="Bitte kurz beschreiben" />}</Label>
        <Label label="Kundenart"><select value={customerTypes} onChange={(e) => setCustomerTypes(e.target.value)}><OptionList items={options.customerTypes} groups={optionGroups.customerTypes} /></select>{needsProfileCustomText(customerTypes) && <input value={customerTypeCustom} onChange={(e) => setCustomerTypeCustom(e.target.value)} placeholder="Bitte kurz beschreiben" />}</Label>
        <Label label="KI-Nutzungsgrad"><select value={aiUsage} onChange={(e) => setAiUsage(e.target.value)}><OptionList items={options.aiUsageMaturity} groups={optionGroups.aiUsageMaturity} /></select>{needsProfileCustomText(aiUsage) && <input value={aiUsageCustom} onChange={(e) => setAiUsageCustom(e.target.value)} placeholder="Bitte kurz beschreiben" />}</Label>
        <button className="button primary" onClick={save}>Speichern und Dashboard öffnen</button>
      </div>
      {highRiskDialogOpen && <div className="modal"><div className="modal-panel high-risk-panel"><h2>Hochrisiko-Signal erkannt</h2><p>Deine Antworten deuten auf einen möglichen Provider-Kontext mit Hochrisiko-Bezug hin. Dieses Tool ist in diesem Fall nur eine Basishilfe für erste Dokumentation und Orientierung.</p><p>Bitte plane vor produktiver Nutzung umfassende Maßnahmen ein, insbesondere externe rechtliche und fachliche Prüfung, Risikomanagement, technische Dokumentation, Governance, Human Oversight, Monitoring und Nachweisführung.</p><div className="modal-actions"><button className="button secondary" onClick={() => setHighRiskDialogOpen(false)}>Antworten prüfen</button><button className="button primary" onClick={save}>Verstanden und speichern</button></div></div></div>}
    </Page>
  );
}

function Tools({ workspace, onRefresh }: { workspace: Workspace; onRefresh: (nextWorkspace?: Workspace) => Promise<void> }) {
  const [toolName, setToolName] = useState("");
  const [category, setCategory] = useState("general_purpose_llm");
  const [context, setContext] = useState("internal_only");
  const [data, setData] = useState("general_ideas");
  const [editingId, setEditingId] = useState<string | null>(null);
  const activeTools = workspace.aiTools.filter((item) => item.status !== "archived");
  const archivedTools = workspace.aiTools.filter((item) => item.status === "archived");

  function resetForm() {
    setEditingId(null);
    setToolName("");
    setCategory("general_purpose_llm");
    setContext("internal_only");
    setData("general_ideas");
  }

  function startEdit(tool: AITool) {
    setEditingId(tool.id);
    setToolName(tool.name ?? "");
    setCategory(tool.category);
    setContext(tool.usage_contexts[0] ?? "client_work");
    setData(tool.data_categories[0] ?? "client_content_project_information");
  }

  async function save() {
    const existing = editingId ? workspace.aiTools.find((item) => item.id === editingId) : undefined;
    const tool: AITool = { id: existing?.id ?? uid("tool"), schema_version: SCHEMA_VERSION, created_at: existing?.created_at ?? now(), updated_at: now(), name: toolName.trim() || undefined, category, status: existing?.status === "archived" ? "active" : data.includes("sensitive") ? "needs_review" : "active", usage_contexts: [context], data_categories: [data], human_review: existing?.human_review ?? "unknown" };
    const action: ActionItem | undefined = !existing ? { id: uid("action"), schema_version: SCHEMA_VERSION, created_at: now(), updated_at: now(), source_type: "tool", source_id: tool.id, title: `Nutzungsregel für ${toolDisplayId(tool.id)} dokumentieren`, category: "tool_governance", priority: "medium", status: "open" } : undefined;
    await db.transaction("rw", [db.aiTools, db.actionItems], async () => {
      await db.aiTools.put(tool);
      if (action) await db.actionItems.put(action);
    });
    resetForm();
    await onRefresh({
      ...workspace,
      aiTools: replaceRecord(workspace.aiTools, tool),
      actionItems: action ? [...workspace.actionItems, action] : workspace.actionItems
    });
  }
  async function duplicate(tool: AITool) {
    const copy: AITool = { ...tool, id: uid("tool"), created_at: now(), updated_at: now(), status: "active" };
    const action: ActionItem = { id: uid("action"), schema_version: SCHEMA_VERSION, created_at: now(), updated_at: now(), source_type: "tool", source_id: copy.id, title: `Nutzungsregel für ${toolDisplayId(copy.id)} dokumentieren`, category: "tool_governance", priority: "medium", status: "open" };
    await db.transaction("rw", [db.aiTools, db.actionItems], async () => {
      await db.aiTools.put(copy);
      await db.actionItems.put(action);
    });
    await onRefresh({ ...workspace, aiTools: [...workspace.aiTools, copy], actionItems: [...workspace.actionItems, action] });
  }
  async function archive(tool: AITool) {
    const archivedTool: AITool = { ...tool, status: "archived", updated_at: now() };
    await db.aiTools.update(tool.id, { status: archivedTool.status, updated_at: archivedTool.updated_at });
    if (editingId === tool.id) resetForm();
    await onRefresh({ ...workspace, aiTools: replaceRecord(workspace.aiTools, archivedTool) });
  }

  async function restore(tool: AITool) {
    const restoredTool: AITool = { ...tool, status: "active", updated_at: now() };
    await db.aiTools.update(tool.id, { status: restoredTool.status, updated_at: restoredTool.updated_at });
    await onRefresh({ ...workspace, aiTools: replaceRecord(workspace.aiTools, restoredTool) });
  }

  async function remove(tool: AITool) {
    if (!confirm("Tool endgültig löschen? Zugehörige abgeleitete Risiken werden neu berechnet; Maßnahmen zu diesem Tool werden entfernt.")) return;
    await db.aiTools.delete(tool.id);
    await db.actionItems.filter((item) => item.source_id === tool.id).delete();
    if (editingId === tool.id) resetForm();
    await onRefresh({
      ...workspace,
      aiTools: removeRecord(workspace.aiTools, tool.id),
      actionItems: workspace.actionItems.filter((item) => item.source_id !== tool.id)
    });
  }
  return (
    <Page title="Tool-Register" action={<button className="button primary" onClick={save}><Plus size={16} /> {editingId ? "Änderungen speichern" : "Tool speichern"}</button>}>
      <div className="form-row">
        <Label label="Toolname (optional, lokal)"><input value={toolName} onChange={(e) => setToolName(e.target.value)} placeholder="z. B. ChatGPT, Claude, DeepL" /></Label>
        <Label label="Tool-Kategorie"><select value={category} onChange={(e) => setCategory(e.target.value)}><OptionList items={options.toolCategories} groups={optionGroups.toolCategories} /></select></Label>
        <Label label="Nutzungskontext"><select value={context} onChange={(e) => setContext(e.target.value)}><OptionList items={options.usageContexts} groups={optionGroups.usageContexts} /></select></Label>
        <Label label="Datenart"><select value={data} onChange={(e) => setData(e.target.value)}><OptionList items={options.dataCategories} groups={optionGroups.dataCategories} /></select></Label>
      </div>
      {editingId && <button className="button secondary" onClick={resetForm}>Bearbeiten abbrechen</button>}
      <ToolTable title="Aktive Tools" tools={activeTools} onEdit={startEdit} onDuplicate={duplicate} onArchive={archive} onDelete={remove} />
      {archivedTools.length > 0 && <ToolTable title="Archivierte Tools" tools={archivedTools} onEdit={startEdit} onDuplicate={duplicate} onRestore={restore} onDelete={remove} archived />}
    </Page>
  );
}

function ToolTable({ title, tools, onEdit, onDuplicate, onArchive, onRestore, onDelete, archived }: {
  title: string;
  tools: AITool[];
  onEdit: (tool: AITool) => void;
  onDuplicate: (tool: AITool) => Promise<void>;
  onArchive?: (tool: AITool) => Promise<void>;
  onRestore?: (tool: AITool) => Promise<void>;
  onDelete: (tool: AITool) => Promise<void>;
  archived?: boolean;
}) {
  return (
    <div className="table-section">
      <h2>{title}</h2>
      {tools.length === 0 ? <div className="empty-state">Noch keine {archived ? "archivierten" : "aktiven"} Tools.</div> : (
        <table>
          <thead><tr><th>ID</th><th>Toolname</th><th>Kategorie</th><th>Status</th><th>Datenarten</th><th>Aktionen</th></tr></thead>
          <tbody>
            {tools.map((tool) => (
              <tr key={tool.id}>
                <td data-label="ID"><span className="record-id">{toolDisplayId(tool.id)}</span></td>
                <td data-label="Toolname">{tool.name || "Nicht angegeben"}</td>
                <td data-label="Kategorie">{modelLabel(tool.category)}</td>
                <td data-label="Status">{modelLabel(tool.status)}</td>
                <td data-label="Datenarten">{tool.data_categories.map(modelLabel).join(", ")}</td>
                <td data-label="Aktionen"><div className="row-actions">
                  <button className="button small" onClick={() => onEdit(tool)}>Bearbeiten</button>
                  <button className="button small" onClick={() => onDuplicate(tool)}>Duplizieren</button>
                  {archived ? <button className="button small" onClick={() => onRestore?.(tool)}>Wiederherstellen</button> : <button className="button small" onClick={() => onArchive?.(tool)}>Archivieren</button>}
                  <button className="button small danger" onClick={() => onDelete(tool)}>Löschen</button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function UseCases({ workspace, features, onRefresh }: { workspace: Workspace; features: AppFeatures; onRefresh: (nextWorkspace?: Workspace) => Promise<void> }) {
  const [searchParams] = useSearchParams();
  const highlightedUseCaseId = searchParams.get("highlight");
  const [category, setCategory] = useState("content_creation");
  const [context, setContext] = useState("internal_only");
  const [toolCategory, setToolCategory] = useState("general_purpose_llm");
  const [toolIds, setToolIds] = useState<string[]>([]);
  const [data, setData] = useState("general_ideas");
  const [output, setOutput] = useState("ideas_drafts");
  const [humanReview, setHumanReview] = useState("always");
  const [description, setDescription] = useState("");
  const [showDescription, setShowDescription] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterContext, setFilterContext] = useState("all");
  const [filterRisk, setFilterRisk] = useState("all");
  const [sortUseCasesBy, setSortUseCasesBy] = useState("updated_desc");
  const activeUseCases = workspace.aiUseCases.filter((item) => item.status !== "archived");
  const archivedUseCases = workspace.aiUseCases.filter((item) => item.status === "archived");
  const matchingTools = workspace.aiTools.filter((tool) => tool.status !== "archived" && tool.category === toolCategory);
  const riskByUseCase = new Map(workspace.riskAssessments.map((risk) => [risk.source_id, risk]));
  const filterOptions = useCaseFilterOptions(activeUseCases, riskByUseCase);
  const visibleActiveUseCases = sortUseCases(activeUseCases.filter((item) => {
    const risk = riskByUseCase.get(item.id);
    return (filterCategory === "all" || item.category === filterCategory)
      && (filterContext === "all" || item.context === filterContext)
      && (filterRisk === "all" || risk?.traffic_light === filterRisk);
  }), sortUseCasesBy, riskByUseCase);

  useEffect(() => {
    if (!highlightedUseCaseId) return;
    requestAnimationFrame(() => {
      document.getElementById(`usecase-${highlightedUseCaseId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [highlightedUseCaseId]);

  function resetForm() {
    setEditingId(null);
    setCategory("content_creation");
    setContext("internal_only");
    setToolCategory("general_purpose_llm");
    setToolIds([]);
    setData("general_ideas");
    setOutput("ideas_drafts");
    setHumanReview("always");
    setDescription("");
    setShowDescription(false);
  }

  function startEdit(useCase: AIUseCase) {
    setEditingId(useCase.id);
    setCategory(useCase.category);
    setContext(useCase.context);
    setToolCategory(useCase.tool_category);
    setToolIds(useCase.tool_ids ?? []);
    setData(useCase.data_categories.join(","));
    setOutput(useCase.output_type);
    setHumanReview(useCase.human_review);
    setDescription(useCase.description ?? "");
    setShowDescription(Boolean(useCase.description));
    requestAnimationFrame(() => {
      document.getElementById("usecase-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  async function save() {
    const existing = editingId ? workspace.aiUseCases.find((item) => item.id === editingId) : undefined;
    const useCase: AIUseCase = {
      id: existing?.id ?? uid("usecase"),
      schema_version: SCHEMA_VERSION,
      created_at: existing?.created_at ?? now(),
      updated_at: now(),
      category,
      context,
      tool_category: toolCategory,
      tool_ids: toolIds.length ? toolIds : undefined,
      data_categories: data.split(",").map((item) => item.trim()).filter(Boolean),
      output_type: output,
      human_review: humanReview,
      customer_interaction: existing?.customer_interaction ?? "no",
      automated_decision_about_people: data === "applicant_employee_data" || output === "decision_support" ? "yes" : "no",
      description: showDescription ? description.trim() || undefined : undefined,
      status: "complete"
    };
    const action: ActionItem | undefined = !existing ? { id: uid("action"), schema_version: SCHEMA_VERSION, created_at: now(), updated_at: now(), source_type: "use_case", source_id: useCase.id, title: "Transparenz- und Human-Review-Regel prüfen", category: "transparency", priority: "high", status: "open" } : undefined;
    await db.transaction("rw", [db.aiUseCases, db.actionItems], async () => {
      await db.aiUseCases.put(useCase);
      if (action) await db.actionItems.put(action);
    });
    resetForm();
    await onRefresh({
      ...workspace,
      aiUseCases: replaceRecord(workspace.aiUseCases, useCase),
      actionItems: action ? [...workspace.actionItems, action] : workspace.actionItems
    });
  }
  async function duplicate(useCase: AIUseCase) {
    const copy: AIUseCase = { ...useCase, id: uid("usecase"), created_at: now(), updated_at: now(), status: "complete" };
    const action: ActionItem = { id: uid("action"), schema_version: SCHEMA_VERSION, created_at: now(), updated_at: now(), source_type: "use_case", source_id: copy.id, title: "Transparenz- und Human-Review-Regel prüfen", category: "transparency", priority: "high", status: "open" };
    await db.transaction("rw", [db.aiUseCases, db.actionItems], async () => {
      await db.aiUseCases.put(copy);
      await db.actionItems.put(action);
    });
    await onRefresh({ ...workspace, aiUseCases: [...workspace.aiUseCases, copy], actionItems: [...workspace.actionItems, action] });
  }
  async function archive(useCase: AIUseCase) {
    const archivedUseCase: AIUseCase = { ...useCase, status: "archived", updated_at: now() };
    await db.aiUseCases.update(useCase.id, { status: archivedUseCase.status, updated_at: archivedUseCase.updated_at });
    if (editingId === useCase.id) resetForm();
    await onRefresh({ ...workspace, aiUseCases: replaceRecord(workspace.aiUseCases, archivedUseCase) });
  }
  async function restore(useCase: AIUseCase) {
    const restoredUseCase: AIUseCase = { ...useCase, status: "complete", updated_at: now() };
    await db.aiUseCases.update(useCase.id, { status: restoredUseCase.status, updated_at: restoredUseCase.updated_at });
    await onRefresh({ ...workspace, aiUseCases: replaceRecord(workspace.aiUseCases, restoredUseCase) });
  }
  async function remove(useCase: AIUseCase) {
    if (!confirm("Use Case endgültig löschen? Zugehörige abgeleitete Risiken werden neu berechnet; Maßnahmen zu diesem Use Case werden entfernt.")) return;
    await db.aiUseCases.delete(useCase.id);
    await db.actionItems.filter((item) => item.source_id === useCase.id).delete();
    if (editingId === useCase.id) resetForm();
    await onRefresh({
      ...workspace,
      aiUseCases: removeRecord(workspace.aiUseCases, useCase.id),
      actionItems: workspace.actionItems.filter((item) => item.source_id !== useCase.id)
    });
  }
  return (
    <Page title="Use Cases" action={<button className="button primary" onClick={save}><Plus size={16} /> {editingId ? "Änderungen speichern" : "Use Case speichern"}</button>}>
      <div className="form-row" id="usecase-form">
        <Label label="Kategorie"><select value={category} onChange={(e) => setCategory(e.target.value)}><OptionList items={options.useCaseCategories} groups={optionGroups.useCaseCategories} /></select></Label>
        <Label label="Kontext"><select value={context} onChange={(e) => setContext(e.target.value)}><OptionList items={options.usageContexts} groups={optionGroups.usageContexts} /></select></Label>
        <Label label="Tool-Kategorie"><select value={toolCategory} onChange={(e) => { setToolCategory(e.target.value); setToolIds([]); }}><OptionList items={options.toolCategories} groups={optionGroups.toolCategories} /></select></Label>
        <Label label="Datenart"><select value={data} onChange={(e) => setData(e.target.value)}><OptionList items={options.dataCategories} groups={optionGroups.dataCategories} /></select></Label>
        <Label label="Output"><select value={output} onChange={(e) => setOutput(e.target.value)}><OptionList items={options.outputTypes} groups={optionGroups.outputTypes} /></select></Label>
        <Label label="Human Review"><select value={humanReview} onChange={(e) => setHumanReview(e.target.value)}><OptionList items={options.humanReviewLevels} groups={optionGroups.humanReviewLevels} /></select></Label>
      </div>
      <div className="field">
        <span>Verknüpfte Tools (optional)</span>
        <div className="tool-link-list">
          {matchingTools.length === 0 && <small>Keine aktiven Tools mit Kategorie „{modelLabel(toolCategory)}“ im Tool-Register hinterlegt.</small>}
          {matchingTools.map((tool) => (
            <label key={tool.id} className="checkbox-field">
              <input
                type="checkbox"
                checked={toolIds.includes(tool.id)}
                onChange={(e) => setToolIds(e.target.checked ? [...toolIds, tool.id] : toolIds.filter((id) => id !== tool.id))}
              />
              <span>{toolReference(tool)}</span>
            </label>
          ))}
        </div>
      </div>
      <label className="checkbox-field">
        <input type="checkbox" checked={showDescription} onChange={(e) => setShowDescription(e.target.checked)} />
        <span>Notiz/Freitext hinzufügen</span>
      </label>
      {showDescription && (
        <label className="field">
          <span>Interne Kurznotiz / Beschreibung</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} placeholder="Optionaler Kontext für Bericht und Wiederfinden." />
          <small>Bitte keine Namen, Kundendaten, E-Mail-Adressen, URLs, Transkripte oder vertraulichen Inhalte eingeben. Diese würden sonst im Bericht erscheinen.</small>
        </label>
      )}
      {editingId && <button className="button secondary" onClick={resetForm}>Bearbeiten abbrechen</button>}
      <UseCaseFilters
        category={filterCategory}
        context={filterContext}
        risk={filterRisk}
        sortBy={sortUseCasesBy}
        total={activeUseCases.length}
        visible={visibleActiveUseCases.length}
        onCategory={setFilterCategory}
        onContext={setFilterContext}
        onRisk={setFilterRisk}
        onSortBy={setSortUseCasesBy}
        onReset={() => { setFilterCategory("all"); setFilterContext("all"); setFilterRisk("all"); setSortUseCasesBy("updated_desc"); }}
        categoryOptions={filterOptions.categories}
        contextOptions={filterOptions.contexts}
        riskOptions={filterOptions.risks}
      />
      <UseCaseTable title="Aktive Use Cases" workspace={workspace} onRefresh={onRefresh} useCases={visibleActiveUseCases} riskByUseCase={riskByUseCase} highlightedId={highlightedUseCaseId} onEdit={startEdit} onDuplicate={duplicate} onArchive={archive} onDelete={remove} summaryAssistEnabled={features.aiAssistEnabled && features.aiAssistUseCaseSummaryEnabled} />
      {archivedUseCases.length > 0 && <UseCaseTable title="Archivierte Use Cases" workspace={workspace} onRefresh={onRefresh} useCases={archivedUseCases} riskByUseCase={riskByUseCase} highlightedId={highlightedUseCaseId} onEdit={startEdit} onDuplicate={duplicate} onRestore={restore} onDelete={remove} summaryAssistEnabled={features.aiAssistEnabled && features.aiAssistUseCaseSummaryEnabled} archived />}
    </Page>
  );
}

function UseCaseFilters({ category, context, risk, sortBy, total, visible, onCategory, onContext, onRisk, onSortBy, onReset, categoryOptions, contextOptions, riskOptions }: {
  category: string;
  context: string;
  risk: string;
  sortBy: string;
  total: number;
  visible: number;
  categoryOptions: string[];
  contextOptions: string[];
  riskOptions: string[];
  onCategory: (value: string) => void;
  onContext: (value: string) => void;
  onRisk: (value: string) => void;
  onSortBy: (value: string) => void;
  onReset: () => void;
}) {
  const [open, setOpen] = useState(false);
  const activeCount = [category !== "all", context !== "all", risk !== "all", sortBy !== "updated_desc"].filter(Boolean).length;
  return (
    <div className="filter-panel">
      <button className="filter-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span><strong>{visible}</strong> von {total} aktiven Use Cases</span>
        <span>{activeCount ? `${activeCount} Filter aktiv` : "Filter & Sortierung"}</span>
      </button>
      {open && (
        <div className="filter-body">
          <div className="form-row compact">
            <Label label="Kategorie"><select value={category} onChange={(e) => onCategory(e.target.value)}><option value="all">Alle</option>{categoryOptions.map((value) => <option value={value} key={value}>{modelLabel(value)}</option>)}</select></Label>
            <Label label="Kontext"><select value={context} onChange={(e) => onContext(e.target.value)}><option value="all">Alle</option>{contextOptions.map((value) => <option value={value} key={value}>{modelLabel(value)}</option>)}</select></Label>
            <Label label="Risiko"><select value={risk} onChange={(e) => onRisk(e.target.value)}><option value="all">Alle</option>{riskOptions.map((value) => <option value={value} key={value}>{modelLabel(value)}</option>)}</select></Label>
            <Label label="Sortierung"><select value={sortBy} onChange={(e) => onSortBy(e.target.value)}><option value="updated_desc">Zuletzt geändert</option><option value="id_asc">ID aufsteigend</option><option value="category_asc">Kategorie A-Z</option><option value="context_asc">Kontext A-Z</option><option value="risk_desc">Risiko hoch zuerst</option><option value="risk_asc">Risiko niedrig zuerst</option></select></Label>
          </div>
          <button className="button small ghost" onClick={onReset}>Zurücksetzen</button>
        </div>
      )}
    </div>
  );
}

function UseCaseTable({ title, workspace, onRefresh, useCases, riskByUseCase, highlightedId, onEdit, onDuplicate, onArchive, onRestore, onDelete, summaryAssistEnabled, archived }: {
  title: string;
  workspace: Workspace;
  onRefresh: () => Promise<void>;
  useCases: AIUseCase[];
  riskByUseCase: Map<string, Workspace["riskAssessments"][number]>;
  highlightedId: string | null;
  onEdit: (useCase: AIUseCase) => void;
  onDuplicate: (useCase: AIUseCase) => Promise<void>;
  onArchive?: (useCase: AIUseCase) => Promise<void>;
  onRestore?: (useCase: AIUseCase) => Promise<void>;
  onDelete: (useCase: AIUseCase) => Promise<void>;
  summaryAssistEnabled: boolean;
  archived?: boolean;
}) {
  return (
    <div className="table-section">
      <h2>{title}</h2>
      {useCases.length === 0 ? <div className="empty-state">Noch keine {archived ? "archivierten" : "aktiven"} Use Cases.</div> : (
        <table>
          <thead><tr><th>ID</th><th>Kategorie</th><th>Kontext</th><th>Tools</th><th>Status</th><th>Risiko</th><th>Datenarten</th><th>Output</th><th className="actions-column">Aktionen</th></tr></thead>
          <tbody>
            {useCases.map((item) => {
              const latestAssist = latestByTimestamp(workspace.generatedDocuments.filter((doc) => doc.type === "use_case_summary" && doc.source_id === item.id));
              const risk = riskByUseCase.get(item.id);
              return (
                <tr key={item.id} id={`usecase-${item.id}`} className={highlightedId === item.id ? "highlight-row" : undefined}>
                  <td data-label="ID"><span className="record-id">{useCaseDisplayId(item.id)}</span></td>
                  <td data-label="Kategorie">{modelLabel(item.category)}</td>
                  <td data-label="Kontext">{modelLabel(item.context)}</td>
                  <td data-label="Tools">{item.tool_ids?.length ? item.tool_ids.map((id) => toolReference(workspace.aiTools.find((tool) => tool.id === id), id)).join(", ") : modelLabel(item.tool_category)}</td>
                  <td data-label="Status">{useCaseStatusLabel(item.status)}</td>
                  <td data-label="Risiko">{risk ? <RiskDot light={risk.traffic_light} /> : "Noch nicht geprüft"}</td>
                  <td data-label="Datenarten">{item.data_categories.map(modelLabel).join(", ")}</td>
                  <td data-label="Output">{modelLabel(item.output_type)}</td>
                  <td data-label="Aktionen"><div className="usecase-actions">
                    <div className="row-actions">
                      <button className="button small" onClick={() => onEdit(item)}>Bearbeiten</button>
                      <button className="button small" onClick={() => onDuplicate(item)}>Duplizieren</button>
                      {archived ? <button className="button small" onClick={() => onRestore?.(item)}>Wiederherstellen</button> : <button className="button small" onClick={() => onArchive?.(item)}>Archivieren</button>}
                    </div>
                    <div className="row-actions subtle-actions">
                      {!archived && summaryAssistEnabled && <AiAssistButton workspace={workspace} task="use_case_summary" module="Use Cases" useCase={item} sourceId={item.id} label="Zusammenfassen" variant="compact" onDone={onRefresh} />}
                      <button className="button small danger ghost" onClick={() => onDelete(item)}>Löschen</button>
                      {!archived && <Link className="risk-jump" to={`/risk?highlight=${item.id}`} aria-label={`Ampel für ${useCaseDisplayId(item.id)} öffnen`} title="Zur Ampel"><ArrowRight size={14} /></Link>}
                    </div>
                  </div>{summaryAssistEnabled && latestAssist && <div className="inline-assist-result"><strong>AI-Zusammenfassung</strong><pre>{latestAssist.content_markdown}</pre></div>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Risk({ workspace }: { workspace: Workspace }) {
  const [searchParams] = useSearchParams();
  const highlightedRiskSourceId = searchParams.get("highlight");
  const useCasesById = new Map(workspace.aiUseCases.map((item) => [item.id, item]));
  useEffect(() => {
    if (!highlightedRiskSourceId) return;
    requestAnimationFrame(() => {
      document.getElementById(`risk-${highlightedRiskSourceId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [highlightedRiskSourceId]);
  return (
    <Page title="Risiko-Ampel">
      {workspace.riskAssessments.length === 0 && <div className="empty-state">Noch keine Use Cases für die Risiko-Ampel vorhanden. Erfasse zuerst einen Use Case.</div>}
      <div className="cards risk-grid">
        {workspace.riskAssessments.map((risk) => {
          const useCase = useCasesById.get(risk.source_id);
          return (
            <Link id={`risk-${risk.source_id}`} className={`card risk-card ${highlightedRiskSourceId === risk.source_id ? "highlight-card" : ""}`} to={`/use-cases?highlight=${risk.source_id}`} key={risk.id} aria-label="Zugehörigen Use Case öffnen">
              <Ampel light={risk.traffic_light} />
              <div>
                <div className="risk-card-header">
                  <span className="record-id">{useCaseDisplayId(risk.source_id)}</span>
                  {useCase && <span>{modelLabel(useCase.category)}</span>}
                </div>
                <Traffic light={risk.traffic_light} />
                <p>{riskExplanation(risk.traffic_light)}</p>
                <ul className="reason-list">
                  {risk.reasons.map((reason) => <li key={reason}>{modelLabel(reason)}</li>)}
                </ul>
                {risk.positive_factors.length > 0 && <p className="positive-note">Positiv: {risk.positive_factors.map(modelLabel).join(", ")}</p>}
                {risk.red_review_points.length > 0 && <strong>Externe Prüfung empfohlen: {risk.red_review_points.map(modelLabel).join(", ")}</strong>}
              </div>
              <span className="risk-jump risk-jump-static" aria-hidden="true"><ArrowRight size={14} /></span>
            </Link>
          );
        })}
      </div>
    </Page>
  );
}

function useCaseStatusLabel(status: string) {
  return status === "complete" ? "Aktiv" : modelLabel(status);
}

function sortUseCases(useCases: AIUseCase[], sortBy: string, riskByUseCase: Map<string, Workspace["riskAssessments"][number]>) {
  const riskValue = (item: AIUseCase) => {
    const light = riskByUseCase.get(item.id)?.traffic_light;
    return light === "red" ? 3 : light === "yellow" ? 2 : light === "green" ? 1 : 0;
  };
  return [...useCases].sort((a, b) => {
    if (sortBy === "id_asc") return useCaseDisplayId(a.id).localeCompare(useCaseDisplayId(b.id));
    if (sortBy === "category_asc") return modelLabel(a.category).localeCompare(modelLabel(b.category), "de");
    if (sortBy === "context_asc") return modelLabel(a.context).localeCompare(modelLabel(b.context), "de");
    if (sortBy === "risk_desc") return riskValue(b) - riskValue(a);
    if (sortBy === "risk_asc") return riskValue(a) - riskValue(b);
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });
}

function useCaseFilterOptions(useCases: AIUseCase[], riskByUseCase: Map<string, Workspace["riskAssessments"][number]>) {
  const unique = (values: string[]) => [...new Set(values)].sort((a, b) => modelLabel(a).localeCompare(modelLabel(b), "de"));
  return {
    categories: unique(useCases.map((item) => item.category)),
    contexts: unique(useCases.map((item) => item.context)),
    risks: unique(useCases.map((item) => riskByUseCase.get(item.id)?.traffic_light).filter(Boolean) as string[])
  };
}

function useCaseDisplayId(id: string) {
  return `UC-${id.replace(/^usecase_/, "").slice(0, 8).toUpperCase()}`;
}

function toolDisplayId(id: string) {
  return `TL-${id.replace(/^tool_/, "").slice(0, 8).toUpperCase()}`;
}

function toolReference(tool: AITool | undefined, id?: string) {
  if (!tool && !id) return "Tool";
  const displayId = toolDisplayId(tool?.id ?? id ?? "");
  return tool?.name ? `${displayId} · ${tool.name}` : `${displayId} · ${modelLabel(tool?.category ?? "tool")}`;
}

function riskExplanation(light: string) {
  if (light === "red") return "Dieser Fall enthält Signale, die nicht lokal als unkritisch eingeordnet werden sollten.";
  if (light === "yellow") return "Dieser Fall ist grundsätzlich nutzbar, sollte aber bewusst dokumentiert und geprüft werden.";
  return "Dieser Fall wirkt nach den aktuellen Angaben überschaubar.";
}

function Actions({ workspace, onRefresh }: { workspace: Workspace; onRefresh: () => Promise<void> }) {
  const [statusFilter, setStatusFilter] = useState("active");
  const toolsById = new Map(workspace.aiTools.map((tool) => [tool.id, tool]));
  const useCasesById = new Map(workspace.aiUseCases.map((item) => [item.id, item]));
  const filteredActions = workspace.actionItems
    .filter((item) => statusFilter === "all" ? true : statusFilter === "active" ? item.status !== "done" : item.status === statusFilter)
    .sort((a, b) => actionSortValue(b) - actionSortValue(a) || new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

  async function updateAction(itemId: string, status: ActionStatus, completionNote: string) {
    await db.actionItems.update(itemId, {
      status,
      completion_note: completionNote.trim() || undefined,
      completed_at: status === "done" ? now() : undefined,
      updated_at: now()
    });
    await onRefresh();
  }

  return (
    <Page title="Maßnahmen">
      <div className="filter-panel">
        <div className="form-row compact">
          <Label label="Status"><select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="active">Aktive Maßnahmen</option><option value="all">Alle Maßnahmen</option><option value="open">Offen</option><option value="in_progress">In Arbeit</option><option value="done">Erledigt</option><option value="needs_external_review">Externe Prüfung</option><option value="postponed">Zurückgestellt</option></select></Label>
        </div>
      </div>
      {filteredActions.length === 0 ? <div className="empty-state">Keine Maßnahmen für diesen Filter.</div> : (
        <div className="action-list">
          {filteredActions.map((item) => (
            <ActionCard key={item.id} item={item} displayTitle={actionDisplayTitle(item, toolsById)} sourceLabel={actionSourceLabel(item, toolsById, useCasesById)} onSave={updateAction} />
          ))}
        </div>
      )}
    </Page>
  );
}

function ActionCard({ item, displayTitle, sourceLabel, onSave }: { item: Workspace["actionItems"][number]; displayTitle: string; sourceLabel: string; onSave: (itemId: string, status: ActionStatus, completionNote: string) => Promise<void> }) {
  const [status, setStatus] = useState(item.status);
  const [note, setNote] = useState(item.completion_note ?? "");
  const riskLink = item.source_type === "use_case" && item.source_id ? `/risk?highlight=${item.source_id}` : undefined;
  useEffect(() => {
    setStatus(item.status);
    setNote(item.completion_note ?? "");
  }, [item.id, item.status, item.completion_note]);
  return (
    <article className="card action-item-card">
      <div>
        <div className="action-card-topline">
          <Status state={item.status} />
          {riskLink && <Link className="risk-jump" to={riskLink} aria-label={`Zur Risiko-Ampel für ${useCaseDisplayId(item.source_id ?? "")}`} title="Zur Risiko-Ampel"><ArrowRight /></Link>}
        </div>
        <h2>{displayTitle}</h2>
        <p>{sourceLabel} · Priorität: {modelLabel(item.priority)}</p>
        {item.completed_at && <p className="privacy-note">Erledigt am {new Date(item.completed_at).toLocaleDateString("de-DE")}</p>}
      </div>
      <div className="action-edit-grid">
        <Label label="Status"><select value={status} onChange={(event) => setStatus(event.target.value as ActionStatus)}><option value="open">Offen</option><option value="in_progress">In Arbeit</option><option value="done">Erledigt</option><option value="needs_external_review">Externe Prüfung</option><option value="postponed">Zurückgestellt</option></select></Label>
        <Label label="Bestätigung / Notiz"><textarea value={note} onChange={(event) => setNote(event.target.value)} rows={3} placeholder="Kurz dokumentieren, was umgesetzt oder geprüft wurde." /></Label>
        <button className="button primary" onClick={() => onSave(item.id, status, note)}>Speichern</button>
      </div>
    </article>
  );
}

function actionSourceLabel(item: Workspace["actionItems"][number], toolsById: Map<string, Workspace["aiTools"][number]>, useCasesById: Map<string, AIUseCase>) {
  if (item.source_type === "use_case" && item.source_id) {
    const useCase = useCasesById.get(item.source_id);
    return `Use Case ${useCaseDisplayId(item.source_id)}${useCase ? ` · ${modelLabel(useCase.category)}` : ""}`;
  }
  if (item.source_type === "tool" && item.source_id) {
    const tool = toolsById.get(item.source_id);
    return `Tool ${toolReference(tool, item.source_id)}`;
  }
  return modelLabel(item.source_type);
}

function actionSortValue(item: Workspace["actionItems"][number]) {
  const priority = item.priority === "high" ? 30 : item.priority === "medium" ? 20 : 10;
  const status = item.status === "open" ? 5 : item.status === "in_progress" ? 4 : item.status === "needs_external_review" ? 3 : item.status === "postponed" ? 2 : 1;
  return priority + status;
}

function RoleCheck({ workspace, features, onRefresh }: { workspace: Workspace; features: AppFeatures; onRefresh: () => Promise<void> }) {
  async function run() {
    const activeUseCases = workspace.aiUseCases.filter((item) => item.status !== "archived");
    const aiActRole = deriveCurrentAiActRole(workspace.businessProfile[0], activeUseCases);
    await db.roleCheckRecords.clear();
    await db.roleCheckRecords.put({ ...deriveRole(workspace.businessProfile[0], activeUseCases), id: "role_check_current", ai_act_role_profile: aiActRole.profile, ai_act_role_confidence: aiActRole.confidence, ai_act_role_finalized: true });
    await onRefresh();
  }
  const role = workspace.roleCheckRecords.find((item) => item.id === "role_check_current") ?? latestByTimestamp(workspace.roleCheckRecords);
  const latestRoleAssist = latestByTimestamp(workspace.generatedDocuments.filter((doc) => doc.type === "role_check_summary"));
  const profile = workspace.businessProfile[0];
  const shownAiActProfile = role?.ai_act_role_finalized ? role.ai_act_role_profile : profile?.preliminary_role_profile;
  const shownAiActConfidence = role?.ai_act_role_finalized ? role.ai_act_role_confidence : profile?.preliminary_role_confidence;
  return (
    <Page title="Rollen-Check" action={<button className="button primary" onClick={run}>Rollenprofil aktualisieren</button>}>
      <section className={`card pre-role-card ${shownAiActProfile === "provider_high_risk" ? "danger" : ""}`}>
        <div>
          <span className="record-id">{role?.ai_act_role_finalized ? "Aktuelles AI-Act-Profil" : "Vorabprofil"}</span>
          <h2>{preliminaryAssessmentLabel(shownAiActProfile)}</h2>
          <p>{role?.ai_act_role_finalized ? "Aktuelle Einordnung aus Onboarding, Business-Profil und aktiven Use Cases. Archivierte Use Cases werden nicht berücksichtigt." : preliminaryRoleDescription(shownAiActProfile)}</p>
        </div>
        <div className="role-verdict">
          <strong>Deployer {shownAiActConfidence?.deployer ?? 0}% · Provider {shownAiActConfidence?.provider ?? 0}%</strong>
          <span>{role?.ai_act_role_finalized ? "Dieses Ergebnis wurde über den Button Rollenprofil aktualisieren berechnet." : "Diese Vorqualifizierung stammt aus dem Onboarding. Die spätere Klassifizierung kann sich durch konkrete Tools, Use Cases, Datenarten und Kundenwirkung verändern."}</span>
        </div>
      </section>
      <div className="detail-grid">
        <section className="card">
          <h2>Deployer</h2>
          <p>Nutzt ein KI-System im eigenen Arbeits- oder Geschäftsprozess, ohne es selbst als KI-System bereitzustellen oder unter eigenem Namen in Verkehr zu bringen.</p>
        </section>
        <section className="card">
          <h2>Provider (nicht-Hochrisiko)</h2>
          <p>Stellt ein KI-System, einen KI-Workflow oder KI-gestützten Service bereit, ohne dass nach den bisherigen Angaben Hochrisiko-Signale erkannt wurden.</p>
        </section>
        <section className="card">
          <h2>Provider (Hochrisiko)</h2>
          <p>Hochrisiko-Signale betreffen insbesondere Personenwirkung oder sensible Einsatzbereiche. Dann ist dieses Kit nur Basishilfe und externe Prüfung ist einzuplanen.</p>
        </section>
      </div>
      {!role ? (
        <div className="card hero-card">
          <strong>Noch nicht geprüft</strong>
          <p>Aktualisiere das Rollenprofil, um Business-Profil und aktive Use Cases lokal einzuordnen.</p>
        </div>
      ) : (
        <>
          <section className={`card role-result-card ${role.requires_external_review ? "needs-review" : ""}`}>
            <div>
              <span className="record-id">Rollenprofil</span>
              <h2>{modelLabel(role.profile)}</h2>
              <p>{roleProfileDescription(role.profile)}</p>
            </div>
            <div className="role-verdict">
              <strong>{role.requires_external_review ? "Externe Prüfung empfohlen" : "Lokale Dokumentation ausreichend"}</strong>
              <span>{role.requires_external_review ? "Das Profil enthält Signale, die nicht als rein interne Standardnutzung behandelt werden sollten." : "Nach den aktuellen Angaben liegt der Schwerpunkt auf interner oder unterstützender KI-Nutzung."}</span>
            </div>
          </section>

          <div className="detail-grid role-detail-grid">
            <section className="card">
              <h2>Warum dieses Profil?</h2>
              {role.signals.length ? <ul className="reason-list">{role.signals.map((signal) => <li key={signal}>{roleSignalExplanation(signal)}</li>)}</ul> : <p>Es wurden keine kunden-, produkt- oder interaktionsbezogenen Signale erkannt.</p>}
            </section>
            <section className="card">
              <h2>Was bedeutet das praktisch?</h2>
              <p>{roleConsequence(role.profile)}</p>
            </section>
            <section className="card">
              <h2>Nächste Schritte</h2>
              <ul className="reason-list">{roleNextSteps(role).map((step) => <li key={step}>{step}</li>)}</ul>
            </section>
          </div>
        </>
      )}
      {features.aiAssistEnabled && <><AiAssistButton workspace={workspace} task="role_check_summary" module="Rollen-Check" onDone={onRefresh} />
      <AiAssistResult title="AI-Erklärung zum Rollenprofil" document={latestRoleAssist} emptyText="Noch keine AI-Erklärung gespeichert. Starte den AI-Assist, um das Rollenprofil verständlich einordnen zu lassen." /></>}
    </Page>
  );
}

function actionDisplayTitle(item: Workspace["actionItems"][number], toolsById: Map<string, Workspace["aiTools"][number]>) {
  if (item.source_type === "tool" && item.source_id && item.title.includes("Tool-Kategorie")) {
    return `Nutzungsregel für ${toolDisplayId(item.source_id)} dokumentieren`;
  }
  return item.title;
}

function roleProfileDescription(profile: string) {
  if (profile === "D") return "KI wird als Produkt, Workflow oder direkte Interaktion mit Kunden bzw. Zielgruppen relevant. Dieses Profil ist am prüfintensivsten.";
  if (profile === "C") return "KI unterstützt Kundenarbeit oder wird in Kundenprozesse eingebunden. Die Nutzung ist nicht mehr nur intern.";
  if (profile === "B") return "Das Business ist kundenorientiert, die KI-Nutzung bleibt aber nach aktueller Dokumentation unterstützend.";
  return "Die KI-Nutzung wirkt nach aktueller Dokumentation überwiegend intern und unterstützend.";
}

function preliminaryRoleDescription(profile?: string) {
  if (profile === "provider_high_risk") return "Vorläufige Einordnung mit Hochrisiko-Signal. Bitte nicht als Freigabe verstehen: umfangreiche Maßnahmen und externe Prüfung sind erforderlich.";
  if (profile === "provider_non_high_risk") return "Vorläufige Einordnung als Provider ohne erkanntes Hochrisiko-Signal. Das kann sich durch konkrete Use Cases und Datenarten ändern.";
  if (profile === "deployer") return "Vorläufige Einordnung als Deployer: KI wird primär als Anwender:in im eigenen Geschäftsprozess genutzt.";
  return "Noch kein Vorabprofil gespeichert. Das Vorab-Assessment kann auf der Profilseite ergänzt werden.";
}
function roleSignalExplanation(signal: string) {
  const explanations: Record<string, string> = {
    ai_system_provided_to_clients: "Ein KI-System, digitaler Workflow oder KI-gestütztes Produkt wird Kunden bereitgestellt.",
    client_work: "Mindestens ein aktiver Use Case ist direkt mit Kundenarbeit oder Kunden-Workflow verbunden.",
    direct_ai_interaction: "Mindestens ein Use Case sieht direkte KI-Interaktion mit Kunden, Besuchern oder Zielgruppen vor.",
    customer_facing_business: "Das Business-Profil ist kunden- oder öffentlichkeitsorientiert."
  };
  return explanations[signal] ?? modelLabel(signal);
}

function roleConsequence(profile: string) {
  if (profile === "D") return "Profil D sollte nicht als reine interne Tool-Nutzung behandelt werden. Vor produktiver Nutzung sind Transparenz, Verantwortlichkeiten, Human Review, Datenflüsse und externe Prüfung besonders wichtig.";
  if (profile === "C") return "Profil C braucht klare Nutzungsregeln für Kundenarbeit: Was wird mit KI erstellt, was wird geprüft, was wird an Kunden übergeben und wo endet die Automatisierung.";
  if (profile === "B") return "Profil B verlangt vor allem saubere Dokumentation der kundenwirksamen Outputs und eine verlässliche menschliche Prüfung vor Nutzung.";
  return "Profil A ist grundsätzlich überschaubar, solange Datenminimierung, lokale Dokumentation und menschliche Prüfung eingehalten werden.";
}

function roleNextSteps(role: Workspace["roleCheckRecords"][number]) {
  if (role.profile === "D") return [
    "Rote Prüfpunkte und betroffene Use Cases auf der Risiko-Ampel prüfen.",
    "Transparenzhinweise und Human-Review-Regeln dokumentieren.",
    "Externe Prüfung vor Freigabe oder Kundenbereitstellung einplanen."
  ];
  if (role.profile === "C") return [
    "Kundenbezogene Use Cases prüfen und Verantwortlichkeiten festhalten.",
    "Maßnahmen für Transparenz und Human Review bearbeiten.",
    "Bei Übergabe von Workflows an Kunden externe Einordnung prüfen."
  ];
  if (role.profile === "B") return [
    "Kundenwirksame Outputs im Use-Case-Register vollständig dokumentieren.",
    "Human Review als Standardregel in der Nutzungsrichtlinie berücksichtigen.",
    "Transparenz-Check nach Änderungen erneut ausführen."
  ];
  return [
    "Tool-Register und Use Cases aktuell halten.",
    "Keine vertraulichen oder personenbezogenen Daten unbedacht eingeben.",
    "Bei neuer Kundenwirkung Rollenprofil erneut aktualisieren."
  ];
}
const literacyCompetenceFields = [
  {
    title: "Verantwortung und Grenzen von KI",
    objective: "KI-Ergebnisse werden als Arbeitsunterstützung verstanden, nicht als automatische Freigabe.",
    relevance: "Solo-Businesses bleiben für Inhalte, Entscheidungen und Außenwirkung verantwortlich, auch wenn ein Tool Formulierungen oder Empfehlungen erzeugt.",
    rules: ["Plausible KI-Ausgaben können fachlich falsch sein.", "Fakten, Quellen und fachliche Aussagen werden vor Nutzung geprüft.", "Rote Prüfsignale werden nicht durch gute Dokumentation aufgehoben."]
  },
  {
    title: "Daten, Vertraulichkeit und Minimierung",
    objective: "Vor jeder KI-Nutzung wird bewusst entschieden, welche Informationen überhaupt eingegeben werden dürfen.",
    relevance: "Kundenbriefings, Projektinformationen, E-Mails, Transkripte und interne Unterlagen können personenbezogene oder vertrauliche Inhalte enthalten.",
    rules: ["Keine Namen, Kundendaten oder vertraulichen Inhalte unbedacht eingeben.", "Daten werden abstrahiert, anonymisiert oder lokal gehalten, wenn das Risiko unklar ist.", "Tool-Kontext und Datenkategorie werden vor Nutzung geprüft."]
  },
  {
    title: "Human Review vor Wirkung nach außen",
    objective: "Kundenwirksame, öffentliche oder entscheidungsnahe Outputs werden vor Verwendung menschlich geprüft.",
    relevance: "Angebote, Website-Texte, Content, Empfehlungen und Kundenkommunikation wirken unmittelbar auf Vertrauen, Haftung und Reputation.",
    rules: ["Kunden- und Öffentlichkeitstexte werden fachlich, sachlich und tonal geprüft.", "Automatische Antworten brauchen klare Grenzen.", "Personenbezogene oder entscheidungsnahe Inhalte werden besonders vorsichtig behandelt."]
  },
  {
    title: "Transparenz und Einordnung",
    objective: "Bei Kunden-, Content- oder Interaktionsbezug wird geprüft, ob ein Hinweis vorbereitet werden muss.",
    relevance: "Transparenz ist kein Marketingtext, sondern eine verständliche Einordnung, wann KI im Prozess beteiligt ist.",
    rules: ["Direkte KI-Interaktion wird nicht als menschliche Kommunikation getarnt.", "Hinweise werden verständlich und nicht überzogen formuliert.", "Es werden keine Compliance-, Zertifizierungs- oder Rechtsgarantien behauptet."]
  },
  {
    title: "Dokumentation und Änderungsdisziplin",
    objective: "KI-Nutzung wird als laufender Arbeitsprozess dokumentiert und bei Änderungen erneut betrachtet.",
    relevance: "Neue Tools, neue Funktionen oder neue Use Cases können die Risikoeinschätzung verändern.",
    rules: ["Tools, Use Cases, Risiken und Maßnahmen bleiben nachvollziehbar dokumentiert.", "Bei neuen Funktionen oder Nutzungsarten wird erneut geprüft.", "Backups sichern die lokale Readiness-Dokumentation."]
  }
];

const literacyPracticeRules = [
  "Ich prüfe KI-Ausgaben vor externer Nutzung fachlich und sachlich.",
  "Ich gebe keine Kundendaten, Namen, vertraulichen Unterlagen oder Transkripte unbedacht in KI-Tools ein.",
  "Ich nutze KI nicht als alleinige Grundlage für Entscheidungen über Personen.",
  "Ich prüfe bei Kundenkontakt, Website, Content oder direkter Interaktion, ob ein Transparenzhinweis nötig ist.",
  "Ich dokumentiere relevante Tools, Use Cases, Risiken und offene Maßnahmen nachvollziehbar.",
  "Ich lasse rote Prüfpunkte sichtbar und behandle sie als Anlass für externe Prüfung."
];

const literacyConfirmations = [
  { id: "responsibility", label: "Ich verstehe, dass KI-Ergebnisse vor Nutzung geprüft werden müssen und keine automatische Freigabe darstellen." },
  { id: "data_minimization", label: "Ich werde personenbezogene, vertrauliche oder sensible Inhalte nicht unbedacht in KI-Tools eingeben." },
  { id: "human_review", label: "Ich werde kundenwirksame und öffentliche Outputs vor Verwendung menschlich prüfen." },
  { id: "transparency", label: "Ich werde bei Kunden-, Content- oder Interaktionsbezug prüfen, ob ein Transparenzhinweis erforderlich oder sinnvoll ist." },
  { id: "tool_understanding", label: "Ich bestätige, dass ein grundlegendes Verständnis über Funktionsweise, typische Grenzen und sachgerechte Nutzung der im Tool-Register erfassten KI-Tools besteht." },
  { id: "documentation", label: "Ich werde Änderungen an Tools oder Use Cases erneut dokumentieren und prüfen." }
];

const literacyQuestions = [
  {
    id: "q1",
    area: "Verantwortung",
    question: "Eine KI-Antwort klingt überzeugend und enthält konkrete Zahlen. Welche Reaktion ist für einen belastbaren Arbeitsprozess am passendsten?",
    options: ["Die Zahlen übernehmen, aber den Text sprachlich noch einmal glätten", "Die Aussage samt Zahlen fachlich prüfen und nur belegbare Inhalte verwenden", "Die Ausgabe verwenden, wenn sie zum bisherigen Bauchgefühl passt"],
    correct: "Die Aussage samt Zahlen fachlich prüfen und nur belegbare Inhalte verwenden",
    explanation: "Plausibilität ersetzt keine fachliche Prüfung. Gerade konkrete Zahlen und Quellen müssen belastbar sein."
  },
  {
    id: "q2",
    area: "Datenminimierung",
    question: "Ein Kundenbriefing enthält Namen, Budget, interne Ziele und Projektkontext. Welche Vorgehensweise passt am besten?",
    options: ["Nur Namen entfernen; Budget und Kontext sind für die KI meist unkritisch", "Das Briefing vollständig nutzen, wenn das Tool einen bekannten Markennamen hat", "Vorher prüfen, ob abstrahierte oder anonymisierte Informationen ausreichen"],
    correct: "Vorher prüfen, ob abstrahierte oder anonymisierte Informationen ausreichen",
    explanation: "Datenminimierung bedeutet, nur die Informationen zu verwenden, die für den Zweck wirklich nötig und vertretbar sind."
  },
  {
    id: "q3",
    area: "Human Review",
    question: "Ein KI-generierter Angebotstext soll an einen Kunden gehen. Welche Prüfung ist am wichtigsten?",
    options: ["Fachliche Richtigkeit, Leistungsversprechen, Tonalität und Kundenerwartung prüfen", "Nur prüfen, ob der Text freundlich genug klingt", "Den Text versenden, wenn vorher schon ähnliche Angebote erstellt wurden"],
    correct: "Fachliche Richtigkeit, Leistungsversprechen, Tonalität und Kundenerwartung prüfen",
    explanation: "Kundenwirksame Texte brauchen mehr als Stilkontrolle: Inhalt, Wirkung und Zusagen müssen stimmen."
  },
  {
    id: "q4",
    area: "Transparenz",
    question: "Ein Website-Chatbot beantwortet Besucherfragen mit KI-Unterstützung. Welche Einschätzung ist professionell?",
    options: ["Ein Hinweis ist nur relevant, wenn personenbezogene Daten abgefragt werden", "Es sollte geprüft werden, ob Besucher klar erkennen müssen, dass KI beteiligt ist", "Ein Hinweis ist eher zu vermeiden, weil er Vertrauen kosten könnte"],
    correct: "Es sollte geprüft werden, ob Besucher klar erkennen müssen, dass KI beteiligt ist",
    explanation: "Bei direkter Interaktion ist Transparenz regelmäßig ein eigener Prüfpunkt, unabhängig von Marketingüberlegungen."
  },
  {
    id: "q5",
    area: "Readiness",
    question: "Der Readiness Score steigt auf 90/100. Welche Aussage ist korrekt?",
    options: ["Der Dokumentationsstand ist weit fortgeschritten, aber keine Rechtskonformität garantiert", "Die dokumentierten KI-Nutzungen sind damit rechtlich freigegeben", "Rote Prüfpunkte verlieren an Bedeutung, wenn der Score hoch ist"],
    correct: "Der Dokumentationsstand ist weit fortgeschritten, aber keine Rechtskonformität garantiert",
    explanation: "Der Score misst Fortschritt der Readiness-Dokumentation, nicht Compliance oder rechtliche Freigabe."
  },
  {
    id: "q6",
    area: "Rote Prüfpunkte",
    question: "Ein Use Case wird rot markiert, weil personenbezogene Wirkung oder sensible Daten möglich sind. Was folgt daraus?",
    options: ["Die Nutzung ist automatisch verboten und muss gelöscht werden", "Der Fall sollte sichtbar bleiben und vor praktischer Nutzung extern geprüft werden", "Eine kurze interne Notiz reicht, wenn Human Review vorgesehen ist"],
    correct: "Der Fall sollte sichtbar bleiben und vor praktischer Nutzung extern geprüft werden",
    explanation: "Rot bedeutet nicht automatisch Verbot, aber auch keine lokale Entwarnung. Der Prüfbedarf bleibt sichtbar."
  },
  {
    id: "q7",
    area: "Änderungen",
    question: "Ein bisheriges Texttool bekommt neue Agentenfunktionen und kann eigenständig Aktionen vorbereiten. Was ist die beste Reaktion?",
    options: ["Tool und betroffene Use Cases erneut prüfen, weil sich Nutzung und Risiko ändern können", "Nur den Toolnamen im Register unverändert lassen, wenn der Anbieter derselbe bleibt", "Erst beim nächsten Jahresreview reagieren, um die Dokumentation stabil zu halten"],
    correct: "Tool und betroffene Use Cases erneut prüfen, weil sich Nutzung und Risiko ändern können",
    explanation: "Neue Funktionen können den Charakter der Nutzung verändern. Dann ist eine erneute Einordnung sinnvoll."
  },
  {
    id: "q8",
    area: "Nachweis",
    question: "Welche Formulierung beschreibt den AI-Literacy-Basisnachweis im MVP am saubersten?",
    options: ["Eine lokale Selbstdokumentation eines allgemeinen Basischecks", "Eine rechtsverbindliche Schulungsbescheinigung für den EU AI Act", "Ein Zertifikat, das die Toolnutzung im eigenen Business freigibt"],
    correct: "Eine lokale Selbstdokumentation eines allgemeinen Basischecks",
    explanation: "Der Nachweis dokumentiert Grundlagenverständnis und Selbstbestätigung, ersetzt aber keine formale Zertifizierung."
  },
  {
    id: "q9",
    area: "Öffentlicher Content",
    question: "KI erstellt einen öffentlichen Fachbeitrag für Website oder Social Media. Worauf sollte die Prüfung besonders achten?",
    options: ["Vor allem darauf, ob der Beitrag möglichst häufig relevante Keywords enthält", "Auf Fakten, Rechte, Tonalität, Außenwirkung und gegebenenfalls Transparenz", "Nur darauf, ob der Text nicht nach KI klingt"],
    correct: "Auf Fakten, Rechte, Tonalität, Außenwirkung und gegebenenfalls Transparenz",
    explanation: "Öffentliche Inhalte wirken nach außen und können fachliche, rechtliche und reputative Risiken berühren."
  },
  {
    id: "q10",
    area: "Local-first",
    question: "Welche Aussage passt am besten zur Local-first-Arbeitsweise dieses Kits?",
    options: ["Die lokale Datenhaltung ersetzt Backups, weil nichts in der Cloud liegt", "AI-Assists dürfen vollständige lokale Daten nutzen, solange der Nutzer zustimmt", "Regelmäßige JSON Backups und bewusste Exporte sichern Portabilität und Wiederherstellung"],
    correct: "Regelmäßige JSON Backups und bewusste Exporte sichern Portabilität und Wiederherstellung",
    explanation: "Local-first reduziert Cloud-Abhängigkeit, macht Backups und bewussten Export aber umso wichtiger."
  }
];
function Literacy({ workspace, onRefresh }: { workspace: Workspace; onRefresh: () => Promise<void> }) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [confirmations, setConfirmations] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const correctCount = literacyQuestions.filter((question) => answers[question.id] === question.correct).length;
  const passed = correctCount >= 8;
  const allAnswered = literacyQuestions.every((question) => answers[question.id]);
  const allConfirmed = literacyConfirmations.every((item) => confirmations[item.id]);
  const latestEvidence = latestByTimestamp(workspace.generatedDocuments.filter((doc) => doc.type === "ai_literacy_basis_documentation"))?.content_markdown
    ?? latestByTimestamp(workspace.literacyRecords.filter(isRealLiteracyEvidence))?.evidence_markdown;

  async function complete() {
    setSubmitted(true);
    if (!allAnswered || !passed || !allConfirmed) return;
    const completedAt = now();
    const evidence = buildLiteracyEvidence(correctCount, completedAt, literacyConfirmations.map((item) => item.label), workspace.businessProfile[0]);
    await db.literacyRecords.put({
      id: uid("literacy"),
      schema_version: SCHEMA_VERSION,
      created_at: completedAt,
      updated_at: completedAt,
      topics_completed: literacyCompetenceFields.map((field) => field.title),
      quiz_score: correctCount,
      passed: true,
      self_confirmed: true,
      answers,
      completed_at: completedAt,
      evidence_markdown: evidence
    });
    await db.generatedDocuments.put({
      id: uid("doc"),
      schema_version: SCHEMA_VERSION,
      created_at: completedAt,
      updated_at: completedAt,
      type: "ai_literacy_basis_documentation",
      title: "AI-Literacy-Basisnachweis",
      content_markdown: evidence,
      status: "ready_to_export"
    });
    await onRefresh();
  }

  return (
    <Page title="AI-Literacy">
      <section className="card literacy-hero">
        <div>
          <span className="record-id">MVP-Basisnachweis</span>
          <h2>KI kompetent, transparent und dokumentiert einsetzen</h2>
          <p>Dieses Modul dokumentiert einen allgemeinen AI-Literacy-Basischeck für Solo-Businesses. Es nutzt bewusst keine eigenen Use-Case-Details, sondern prüft Grundverständnis, Arbeitsregeln und Selbstbestätigung.</p>
        </div>
        <div className="literacy-scorebox">
          <strong>{correctCount}/10</strong>
          <span>{passed ? "Schwelle erreicht" : "Mindestwert: 8/10"}</span>
        </div>
      </section>

      <section className="literacy-section">
        <div className="section-heading"><h2>Kompetenzfelder</h2></div>
        <div className="literacy-grid">
          {literacyCompetenceFields.map((field, index) => (
            <article className="card literacy-card" key={field.title}>
              <span className="step-badge">{index + 1}</span>
              <h3>{field.title}</h3>
              <p><strong>Ziel:</strong> {field.objective}</p>
              <p>{field.relevance}</p>
              <ul className="reason-list">{field.rules.map((rule) => <li key={rule}>{rule}</li>)}</ul>
            </article>
          ))}
        </div>
      </section>

      <section className="card literacy-standard">
        <h2>Praxisstandard für die eigene KI-Nutzung</h2>
        <p>Diese Regeln bilden den dokumentierten Mindeststandard für die weitere Arbeit mit KI-Tools im MVP.</p>
        <div className="practice-rule-grid">
          {literacyPracticeRules.map((rule) => <div className="practice-rule" key={rule}><CheckCircle2 /> <span>{rule}</span></div>)}
        </div>
      </section>

      <section className="card quiz-card">
        <div className="section-heading">
          <div>
            <h2>Kontrollfragen</h2>
            <p>Der Basisnachweis wird ab 8 von 10 richtigen Antworten gespeichert. Antworten können vor dem Speichern geändert werden.</p>
          </div>
          <span className={`status ${passed ? "complete" : "in_progress"}`}>{correctCount}/10 richtig</span>
        </div>
        <div className="literacy-question-list">
          {literacyQuestions.map((question) => {
            const selected = answers[question.id];
            const isWrong = submitted && selected && selected !== question.correct;
            return (
              <fieldset className="quiz-question" key={question.id}>
                <legend><span>{question.area}</span>{question.question}</legend>
                <div className="answer-grid">
                  {question.options.map((option) => (
                    <label className="radio-option" key={option}>
                      <input type="radio" name={question.id} value={option} checked={selected === option} onChange={() => setAnswers((current) => ({ ...current, [question.id]: option }))} />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
                {submitted && selected === question.correct && <p className="positive-note">Richtig eingeordnet. {question.explanation}</p>}
                {isWrong && <p className="danger-text">Bitte nacharbeiten: {question.explanation}</p>}
              </fieldset>
            );
          })}
        </div>
      </section>

      <section className="card literacy-confirmation">
        <h2>Selbstbestätigung</h2>
        <p>Der Nachweis wird erst gespeichert, wenn der Wissenscheck bestanden und alle Grundregeln bewusst bestätigt wurden.</p>
        <div className="confirmation-list">
          {literacyConfirmations.map((item) => (
            <label className="checkbox-field" key={item.id}>
              <input type="checkbox" checked={Boolean(confirmations[item.id])} onChange={(event) => setConfirmations((current) => ({ ...current, [item.id]: event.target.checked }))} />
              <span>{item.label}</span>
            </label>
          ))}
        </div>
        {submitted && !allAnswered && <p className="danger-text">Bitte alle Kontrollfragen beantworten.</p>}
        {submitted && allAnswered && !passed && <p className="danger-text">Der Wissenscheck ist noch nicht bestanden. Prüfe die markierten Antworten erneut.</p>}
        {submitted && passed && !allConfirmed && <p className="danger-text">Bitte alle Selbstbestätigungen aktivieren.</p>}
        <div className="row-actions"><button className="button primary" onClick={complete}>Basisnachweis lokal speichern</button></div>
      </section>

      <section className="card hero-card">
        <strong>Basisnachweis exportieren</strong>
        <p>Exportiert den zuletzt gespeicherten AI-Literacy-Basisnachweis separat als Markdown oder PDF.</p>
        <div className="row-actions">
          <button className="button secondary" disabled={!latestEvidence} onClick={() => latestEvidence && downloadFile(datedFilename("ai-literacy-basisnachweis", "md"), latestEvidence, "text/markdown")}>Markdown exportieren</button>
          <button className="button secondary" disabled={!latestEvidence} onClick={() => latestEvidence && downloadFile(datedFilename("ai-literacy-basisnachweis", "pdf"), markdownToPdf(latestEvidence, "AI-Literacy-Basisnachweis"), "application/pdf")}>PDF exportieren</button>
        </div>
      </section>
    </Page>
  );
}

function buildLiteracyEvidence(correctCount: number, completedAt: string, confirmations: string[], profile?: BusinessProfile) {
  return [
    "# AI-Literacy-Basisnachweis",
    "",
    "Dieser Nachweis dokumentiert einen lokalen AI-Literacy-Basischeck im AI-Act Readiness Kit. Er ist kein offizielles Zertifikat, keine Rechtsberatung und keine Garantie für EU-AI-Act-Konformität.",
    "",
    "## Dokumentationsprofil",
    ...businessProfileEvidenceLines(profile),
    "",
    `Abgeschlossen am: ${new Date(completedAt).toLocaleDateString("de-DE")}`,
    `Wissenscheck: ${correctCount}/10 richtige Antworten`,
    "Bestehensschwelle: mindestens 8/10 richtige Antworten und vollständige Selbstbestätigung.",
    "",
    "## Behandelte Kompetenzfelder",
    ...literacyCompetenceFields.map((field) => `- ${field.title}: ${field.objective}`),
    "",
    "## Dokumentierter Praxisstandard",
    ...literacyPracticeRules.map((rule) => `- ${rule}`),
    "",
    "## Selbstbestätigung",
    ...confirmations.map((confirmation) => `- Bestätigt: ${confirmation}`),
    "",
    "## Einordnung",
    "- Der Nachweis belegt einen allgemeinen Basischeck, keine individuelle rechtliche Prüfung.",
    "- Er nutzt keine spezifischen Kundendaten, Projektinhalte oder eigenen Use-Case-Freitexte.",
    "- Bei roten Prüfpunkten, Personenwirkung, sensiblen Daten oder unklarer Einordnung bleibt externe Prüfung empfohlen."
  ].join("\n");
}
function Transparency({ workspace, features, onRefresh }: { workspace: Workspace; features: AppFeatures; onRefresh: () => Promise<void> }) {
  const activeUseCases = workspace.aiUseCases.filter((item) => item.status !== "archived");
  async function run() {
    await db.transparencyChecks.clear();
    await db.transparencyChecks.put({ ...deriveTransparency(activeUseCases), id: "transparency_check_current" });
    await onRefresh();
  }
  const check = workspace.transparencyChecks.find((item) => item.id === "transparency_check_current") ?? latestByTimestamp(workspace.transparencyChecks);
  const latestTransparencyAssist = latestByTimestamp(workspace.generatedDocuments.filter((doc) => doc.type === "transparency_customer_project_notice"));
  return (
    <Page title="Transparenz-Check" action={<button className="button primary" onClick={run}>Transparenz prüfen</button>}>
      <div className="card hero-card">
        <strong>{check ? modelLabel(check.status) : "Noch nicht geprüft"}</strong>
        <p>{transparencyExplanation(check, activeUseCases.length)}</p>
      </div>
      {check && (
        <div className="detail-grid">
          <div className="card">
            <h2>Warum?</h2>
            <ul className="reason-list">
              {transparencyReasons(check).map((reason) => <li key={reason}>{reason}</li>)}
            </ul>
          </div>
          <div className="card">
            <h2>Empfohlene Hinweise</h2>
            {check.recommended_notices.length ? <ul className="reason-list">{check.recommended_notices.map((notice) => <li key={notice}>{notice}</li>)}</ul> : <p>Nach den aktuellen Use Cases ist kein eigener Transparenzhinweis erkennbar.</p>}
          </div>
          <div className="card">
            <h2>Nächster Schritt</h2>
            <p>{transparencyNextStep(check)}</p>
          </div>
        </div>
      )}
      {features.aiAssistEnabled && <><AiAssistButton workspace={workspace} task="transparency_customer_project_notice" module="Transparenz" onDone={onRefresh} />
      <AiAssistResult title="AI-Textvorschlag für Transparenzhinweis" document={latestTransparencyAssist} emptyText="Noch kein AI-Textvorschlag gespeichert. Starte den AI-Assist, um einen Formulierungsvorschlag für den passenden Hinweis erzeugen zu lassen." /></>}
    </Page>
  );
}

function transparencyExplanation(check: Workspace["transparencyChecks"][number] | undefined, useCaseCount: number) {
  if (!check) return "Klicke auf Transparenz prüfen, um aktive Use Cases lokal auf Kunden-, Content- und Interaktionsbezug zu prüfen.";
  if (useCaseCount === 0) return "Es sind noch keine aktiven Use Cases vorhanden. Erfasse zuerst mindestens einen Use Case.";
  if (check.status === "external_review_recommended") return "Mindestens ein Use Case enthält sensible oder besonders prüfbedürftige Signale. Die App erstellt hier keine Freigabe, sondern empfiehlt externe Prüfung.";
  if (check.status === "notice_recommended") return "Mindestens ein Use Case hat Kunden-, Öffentlichkeits- oder Interaktionsbezug. Dafür sollte ein passender Hinweis vorbereitet werden.";
  return "Die aktiven Use Cases zeigen derzeit keinen klaren Kunden-, Öffentlichkeits- oder direkten Interaktionsbezug.";
}

function transparencyReasons(check: Workspace["transparencyChecks"][number]) {
  return [
    check.customer_facing === "yes" && "Kunden- oder Angebotsbezug erkannt.",
    check.public_output === "yes" && "Öffentlicher Content oder Website-/Content-Ausgabe erkannt.",
    check.direct_ai_interaction === "yes" && "Direkte KI-Interaktion mit Personen erkannt.",
    check.status === "external_review_recommended" && "Sensible oder rote Prüfsignale vorhanden."
  ].filter(Boolean) as string[];
}

function transparencyNextStep(check: Workspace["transparencyChecks"][number]) {
  if (check.status === "external_review_recommended") return "Hinweisbedarf dokumentieren und vor Nutzung oder Veröffentlichung extern prüfen lassen.";
  if (check.status === "notice_recommended") return "Passenden Hinweistext für Angebot, Website, Content oder direkte Interaktion erstellen und im jeweiligen Kanal verwenden.";
  return "Aktuelle Einschätzung dokumentieren und bei neuen Use Cases erneut prüfen.";
}

function Documents({ workspace, features, policyConfig, onRefresh }: { workspace: Workspace; features: AppFeatures; policyConfig: PolicyConfig; onRefresh: () => Promise<void> }) {
  const [includeArchived, setIncludeArchived] = useState(false);
  const dossier = useMemo(() => buildDossierMarkdown(workspace, includeArchived, { includeAiAssist: features.aiAssistEnabled }), [workspace, includeArchived, features.aiAssistEnabled]);
  const policy = useMemo(() => buildPolicyMarkdown(workspace, includeArchived, policyConfig), [workspace, includeArchived, policyConfig]);
  const latestDocumentAssist = latestByTimestamp(workspace.generatedDocuments.filter((doc) => doc.type === "document_executive_summary"));
  async function saveDocs() {
    const docs: GeneratedDocument[] = [
      { id: uid("doc"), schema_version: SCHEMA_VERSION, created_at: now(), updated_at: now(), type: "readiness_dossier", title: "Workspace Summary", content_markdown: dossier, status: "ready_to_export" },
      { id: uid("doc"), schema_version: SCHEMA_VERSION, created_at: now(), updated_at: now(), type: "policy", title: "KI-Nutzungsrichtlinie", content_markdown: policy, status: "ready_to_export" }
    ];
    await db.generatedDocuments.bulkPut(docs);
    await onRefresh();
  }
  return (
    <Page title="Dokumente" action={<button className="button primary" onClick={saveDocs}>Dokumente erzeugen</button>}>
      <label className="checkbox-field"><input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} /><span>Archivierte Einträge in Workspace Summary und Richtlinie listen</span></label>
      <div className="document-section">
        <div className="section-heading">
          <h2>Workspace Summary</h2>
          <div className="row-actions">
            <button className="button secondary" onClick={() => downloadFile(datedFilename("workspace-summary", "md"), dossier, "text/markdown")}>Markdown exportieren</button>
            <button className="button secondary" onClick={() => downloadFile(datedFilename("workspace-summary", "pdf"), markdownToPdf(dossier, "AI-Act Readiness Summary"), "application/pdf")}>PDF exportieren</button>
          </div>
        </div>
        <DocumentPreview markdown={dossier} type="dossier" />
      </div>
      <div className="document-section">
        <div className="section-heading">
          <h2>KI-Nutzungsrichtlinie</h2>
          <div className="row-actions">
            <button className="button secondary" onClick={() => downloadFile(datedFilename("ki-nutzungsrichtlinie", "md"), policy, "text/markdown")}>Markdown exportieren</button>
            <button className="button secondary" onClick={() => downloadFile(datedFilename("ki-nutzungsrichtlinie", "pdf"), markdownToPdf(policy, "KI-Nutzungsrichtlinie"), "application/pdf")}>PDF exportieren</button>
          </div>
        </div>
        <DocumentPreview markdown={policy} />
      </div>
      {features.aiAssistEnabled && <><div className="documents-assist-action"><AiAssistButton workspace={workspace} task="document_executive_summary" module="Dokumente" onDone={onRefresh} /></div>
      <AiAssistResult title="AI-Kurzzusammenfassung" document={latestDocumentAssist} emptyText="Noch keine AI-Kurzzusammenfassung gespeichert. Starte den AI-Assist, um eine kompakte Zusammenfassung des Readiness-Stands erzeugen zu lassen." /></>}
    </Page>
  );
}

function ExportImport({ workspace, features, policyConfig, onRefresh }: { workspace: Workspace; features: AppFeatures; policyConfig: PolicyConfig; onRefresh: () => Promise<void> }) {
  const [includeArchived, setIncludeArchived] = useState(false);
  const literacyEvidence = latestByTimestamp(workspace.generatedDocuments.filter((doc) => doc.type === "ai_literacy_basis_documentation"))?.content_markdown
    ?? latestByTimestamp(workspace.literacyRecords.filter(isRealLiteracyEvidence))?.evidence_markdown;
  async function exportJson() {
    const data = await getWorkspace();
    const filename = datedFilename("ai-act-readiness-backup", "json");
    await db.appSettings.update("settings_default", { last_backup_at: now(), updated_at: now() });
    await db.exportHistory.put({ id: uid("export"), schema_version: SCHEMA_VERSION, created_at: now(), updated_at: now(), export_type: "json", filename });
    await downloadFile(filename, JSON.stringify(data, null, 2), "application/json");
    await onRefresh();
  }
  async function importJson(file?: File) {
    if (!file) return;
    const parsed = JSON.parse(await file.text()) as Workspace;
    if (!confirm("Aktuelle lokale Daten werden ersetzt. Fortfahren?")) return;
    await replaceWorkspace(parsed, file.name);
    await onRefresh();
  }
  async function exportAll() {
    const dossier = buildDossierMarkdown(workspace, includeArchived, { includeAiAssist: features.aiAssistEnabled });
    const policy = buildPolicyMarkdown(workspace, includeArchived, policyConfig);
    const files: Array<{ name: string; content: string | Blob }> = [
      { name: datedFilename("tool-register", "csv"), content: toolsToCsv(workspace, includeArchived) },
      { name: datedFilename("massnahmen", "csv"), content: actionItemsToCsv(workspace) },
      { name: datedFilename("workspace-summary", "md"), content: dossier },
      { name: datedFilename("workspace-summary", "pdf"), content: markdownToPdf(dossier, "AI-Act Readiness Summary") },
      { name: datedFilename("ki-nutzungsrichtlinie", "md"), content: policy },
      { name: datedFilename("ki-nutzungsrichtlinie", "pdf"), content: markdownToPdf(policy, "KI-Nutzungsrichtlinie") }
    ];
    if (literacyEvidence) {
      files.push(
        { name: datedFilename("ai-literacy-basisnachweis", "md"), content: literacyEvidence },
        { name: datedFilename("ai-literacy-basisnachweis", "pdf"), content: markdownToPdf(literacyEvidence, "AI-Literacy-Basisnachweis") }
      );
    }
    await downloadFile(datedFilename("ai-act-readiness-export", "zip"), await zipFiles(files), "application/zip");
  }
  return (
    <Page title="Export / Import" action={<div className="export-all-action"><button className="button primary" onClick={exportAll}><FileDown /> Alles exportieren</button><small>Nur CSV, MD & PDF - ohne Backup</small></div>}>
      <div className="export-layout">
        <section className="export-section">
          <div className="section-heading"><div><div className="heading-with-label"><h2>Backup & Wiederherstellung</h2><span className="info-tooltip" tabIndex={0} title="PRIVACY-FIRST: Alle Daten dieses Workspace existieren nur lokal in diesem Browser auf diesem Gerät. Für die Nutzung auf einem anderen Gerät oder in einem anderen Browser exportierst du ein JSON Backup und importierst es dort wieder.">Wichtige Hinweise</span></div><p>Vollständige lokale Workspace-Daten als kanonisches Backup.</p></div></div>
          <div className="export-card-grid two">
            <button className="card export-card" onClick={exportJson}><Archive /><span><strong>JSON Backup exportieren</strong><small>Vollständiger Workspace für Wiederherstellung oder Umzug.</small></span></button>
            <label className="card export-card upload-card"><Upload /><span><strong>JSON Backup importieren</strong><small>Ersetzt die aktuellen lokalen Daten nach Bestätigung.</small></span><input type="file" accept="application/json" onChange={(e) => importJson(e.target.files?.[0])} /></label>
          </div>
        </section>

        <section className="export-section">
          <div className="section-heading"><div><h2>Datenexporte</h2><p>Strukturierte Tabellen für Register, Prüfung oder Weiterverarbeitung.</p></div></div>
          <label className="checkbox-field export-option"><input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} /><span>Archivierte Einträge in CSV, Workspace Summary und Richtlinie listen</span></label>
          <div className="export-card-grid">
            <button className="card export-card" onClick={() => downloadFile(datedFilename("tool-register", "csv"), toolsToCsv(workspace, includeArchived), "text/csv")}><FileDown /><span><strong>Tool-Register CSV</strong><small>Alle dokumentierten KI-Tools als Tabelle.</small></span></button>
            <button className="card export-card" onClick={() => downloadFile(datedFilename("massnahmen", "csv"), actionItemsToCsv(workspace), "text/csv")}><FileDown /><span><strong>Maßnahmen CSV</strong><small>Status, Priorität und Bestätigung der Maßnahmen.</small></span></button>
          </div>
        </section>

        <section className="export-section">
          <div className="section-heading"><div><h2>Dokumente</h2><p>Exportfertige Nachweise und Richtlinien als Markdown oder PDF.</p></div></div>
          <div className="export-card-grid">
            <button className="card export-card" onClick={() => downloadFile(datedFilename("workspace-summary", "md"), buildDossierMarkdown(workspace, includeArchived, { includeAiAssist: features.aiAssistEnabled }), "text/markdown")}><FileText /><span><strong>Workspace Summary</strong><small>Markdown</small></span></button>
            <button className="card export-card" onClick={() => downloadFile(datedFilename("workspace-summary", "pdf"), markdownToPdf(buildDossierMarkdown(workspace, includeArchived, { includeAiAssist: features.aiAssistEnabled }), "AI-Act Readiness Summary"), "application/pdf")}><FileDown /><span><strong>Workspace Summary</strong><small>PDF</small></span></button>
            <button className="card export-card" onClick={() => downloadFile(datedFilename("ki-nutzungsrichtlinie", "md"), buildPolicyMarkdown(workspace, includeArchived, policyConfig), "text/markdown")}><FileText /><span><strong>KI-Nutzungsrichtlinie</strong><small>Markdown</small></span></button>
            <button className="card export-card" onClick={() => downloadFile(datedFilename("ki-nutzungsrichtlinie", "pdf"), markdownToPdf(buildPolicyMarkdown(workspace, includeArchived, policyConfig), "KI-Nutzungsrichtlinie"), "application/pdf")}><FileDown /><span><strong>KI-Nutzungsrichtlinie</strong><small>PDF</small></span></button>
            <button className="card export-card" disabled={!literacyEvidence} onClick={() => literacyEvidence && downloadFile(datedFilename("ai-literacy-basisnachweis", "md"), literacyEvidence, "text/markdown")}><FileText /><span><strong>AI-Literacy-Basisnachweis</strong><small>Markdown</small></span></button>
            <button className="card export-card" disabled={!literacyEvidence} onClick={() => literacyEvidence && downloadFile(datedFilename("ai-literacy-basisnachweis", "pdf"), markdownToPdf(literacyEvidence, "AI-Literacy-Basisnachweis"), "application/pdf")}><FileDown /><span><strong>AI-Literacy-Basisnachweis</strong><small>PDF</small></span></button>
          </div>
        </section>
      </div>
    </Page>
  );
}

type HandbookHeading = {
  id: string;
  level: 1 | 2 | 3;
  text: string;
};

type HandbookBlock =
  | { type: "heading"; level: 1 | 2 | 3; text: string; id: string }
  | { type: "paragraph"; text: string }
  | { type: "ul" | "ol"; items: string[] };
type HandbookSearchResult = {
  id: string;
  title: string;
  excerpt: string;
};

function HandbookPage() {
  const [markdown, setMarkdown] = useState("");
  const [error, setError] = useState("");
  const [tocOpen, setTocOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const tocRef = useRef<HTMLDivElement | null>(null);
  const topRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let active = true;
    fetch("/handbuch/ben-ai-act-readiness-kit-handbuch.md")
      .then((response) => {
        if (!response.ok) throw new Error("Handbuch konnte nicht geladen werden.");
        return response.text();
      })
      .then((text) => {
        if (active) setMarkdown(text);
      })
      .catch((loadError) => {
        if (active) setError(loadError instanceof Error ? loadError.message : "Handbuch konnte nicht geladen werden.");
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!tocOpen) return;
    const closeOnOutside = (event: MouseEvent) => {
      if (tocRef.current && !tocRef.current.contains(event.target as Node)) setTocOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setTocOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [tocOpen]);

  const parsed = useMemo(() => parseHandbookMarkdown(markdown), [markdown]);
  const searchResults = useMemo(() => searchHandbook(parsed.blocks, searchQuery), [parsed.blocks, searchQuery]);
  const trimmedSearchQuery = searchQuery.trim();

  function jumpToHeading(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTocOpen(false);
  }

  function jumpToSearchResult(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <Page title="Handbuch">
      <div ref={topRef} />
      <div className="handbook-shell">
        <div className="handbook-toc" ref={tocRef}>
          <button className="button secondary handbook-toc-trigger" type="button" onClick={() => setTocOpen((open) => !open)} aria-expanded={tocOpen}>
            <BookOpen /> Inhaltsverzeichnis <ChevronDown className={tocOpen ? "open" : ""} />
          </button>
          {tocOpen && <div className="handbook-toc-menu">
            {parsed.headings.map((heading) => (
              <button type="button" key={heading.id} className={`handbook-toc-item level-${heading.level}`} onClick={() => jumpToHeading(heading.id)}>
                {heading.text}
              </button>
            ))}
          </div>}
        </div>
        <div className="handbook-search" role="search">
          <Search />
          <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Handbuch durchsuchen" aria-label="Handbuch durchsuchen" />
          {searchQuery && <button type="button" onClick={() => setSearchQuery("")} aria-label="Suche leeren"><X /></button>}
        </div>
        {trimmedSearchQuery.length >= 2 && <div className="handbook-search-results">
          <div className="handbook-search-summary">{searchResults.length ? `${searchResults.length} Treffer` : "Keine Treffer"}</div>
          {searchResults.map((result, index) => (
            <button type="button" key={`${result.id}-${index}`} onClick={() => jumpToSearchResult(result.id)}>
              <strong>{result.title}</strong>
              <span>{result.excerpt}</span>
            </button>
          ))}
        </div>}
        {error && <div className="warning-card"><AlertTriangle /> {error}</div>}
        {!error && !markdown && <div className="empty-state">Handbuch wird geladen...</div>}
        {!error && markdown && <article className="handbook-content">
          {parsed.blocks.map((block, index) => renderHandbookBlock(block, index))}
        </article>}
        <button className="handbook-top-button" type="button" onClick={() => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} aria-label="Zum Anfang des Handbuchs">
          <ArrowUp />
        </button>
      </div>
    </Page>
  );
}

function parseHandbookMarkdown(markdown: string): { blocks: HandbookBlock[]; headings: HandbookHeading[] } {
  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const blocks: HandbookBlock[] = [];
  const headings: HandbookHeading[] = [];
  const slugCounts = new Map<string, number>();
  let paragraph: string[] = [];
  let listItems: string[] = [];
  let listType: "ul" | "ol" | null = null;

  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push({ type: "paragraph", text: paragraph.join(" ") });
    paragraph = [];
  };
  const flushList = () => {
    if (!listType || !listItems.length) return;
    blocks.push({ type: listType, items: listItems });
    listItems = [];
    listType = null;
  };
  const uniqueId = (text: string) => {
    const base = slugify(text) || "abschnitt";
    const count = slugCounts.get(base) ?? 0;
    slugCounts.set(base, count + 1);
    return count ? `${base}-${count + 1}` : base;
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushParagraph();
      flushList();
      continue;
    }
    const heading = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      flushParagraph();
      flushList();
      const level = heading[1].length as 1 | 2 | 3;
      const headingText = heading[2].trim();
      const id = uniqueId(headingText);
      blocks.push({ type: "heading", level, text: headingText, id });
      headings.push({ id, level, text: headingText });
      continue;
    }
    const unordered = trimmed.match(/^-\s+(.+)$/);
    const ordered = trimmed.match(/^\d+\.\s+(.+)$/);
    if (unordered || ordered) {
      flushParagraph();
      const nextType = unordered ? "ul" : "ol";
      if (listType && listType !== nextType) flushList();
      listType = nextType;
      listItems.push((unordered?.[1] ?? ordered?.[1] ?? "").trim());
      continue;
    }
    flushList();
    paragraph.push(trimmed);
  }
  flushParagraph();
  flushList();
  return { blocks, headings };
}

function searchHandbook(blocks: HandbookBlock[], query: string): HandbookSearchResult[] {
  const normalizedQuery = normalizeSearchText(query);
  if (normalizedQuery.length < 2) return [];
  const results: HandbookSearchResult[] = [];
  let currentHeading: HandbookHeading | undefined;

  for (const block of blocks) {
    if (block.type === "heading") currentHeading = { id: block.id, level: block.level, text: block.text };
    const searchableText = block.type === "heading" ? block.text : block.type === "paragraph" ? block.text : block.items.join(" ");
    if (!normalizeSearchText(searchableText).includes(normalizedQuery)) continue;
    const target = block.type === "heading" ? { id: block.id, text: block.text } : currentHeading;
    if (!target) continue;
    results.push({
      id: target.id,
      title: target.text,
      excerpt: buildSearchExcerpt(searchableText, query)
    });
    if (results.length >= 20) break;
  }
  return results;
}

function buildSearchExcerpt(text: string, query: string) {
  const lowerText = text.toLowerCase();
  const lowerQuery = query.trim().toLowerCase();
  const index = lowerQuery ? lowerText.indexOf(lowerQuery) : -1;
  if (index < 0) return text.length > 150 ? `${text.slice(0, 150)}...` : text;
  const start = Math.max(0, index - 58);
  const end = Math.min(text.length, index + lowerQuery.length + 92);
  return `${start > 0 ? "..." : ""}${text.slice(start, end)}${end < text.length ? "..." : ""}`;
}

function normalizeSearchText(text: string) {
  return text
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}
function slugify(text: string) {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function renderHandbookBlock(block: HandbookBlock, index: number) {
  if (block.type === "heading") {
    const Tag = `h${block.level}` as "h1" | "h2" | "h3";
    return <Tag key={block.id} id={block.id}>{renderInlineMarkdown(block.text)}</Tag>;
  }
  if (block.type === "paragraph") return <p key={`p-${index}`}>{renderInlineMarkdown(block.text)}</p>;
  const ListTag = block.type;
  return <ListTag key={`${block.type}-${index}`}>{block.items.map((item, itemIndex) => <li key={`${item}-${itemIndex}`}>{renderInlineMarkdown(item)}</li>)}</ListTag>;
}

function renderInlineMarkdown(text: string) {
  return text.split(/(`[^`]+`)/g).filter(Boolean).map((part, index) => {
    if (part.startsWith("`") && part.endsWith("`")) return <code key={index}>{part.slice(1, -1)}</code>;
    return <span key={index}>{part}</span>;
  });
}
function SettingsPage({ workspace, features, gatingConfig, licenseAccess, onLicenseRefresh, onRefresh }: { workspace: Workspace; features: AppFeatures; gatingConfig: GatingConfig; licenseAccess: LicenseAccessState; onLicenseRefresh: () => Promise<void>; onRefresh: () => Promise<void> }) {
  const settings = workspace.appSettings[0];
  const userAiAssistEnabled = settings?.ai_assist_enabled_by_user === true;
  const [licenseBusy, setLicenseBusy] = useState(false);
  async function reset() {
    if (!confirm("Lokale Daten wirklich löschen? Bitte vorher ein JSON Backup erstellen.")) return;
    await clearLocalData();
    await onRefresh();
  }
  async function toggleAiAssist() {
    if (!settings) return;
    await db.appSettings.update(settings.id, { ai_assist_enabled_by_user: !userAiAssistEnabled, updated_at: now() });
    await onRefresh();
  }
  async function refreshLicenseStatus() {
    setLicenseBusy(true);
    try {
      await onLicenseRefresh();
    } finally {
      setLicenseBusy(false);
    }
  }
  async function forgetLicense() {
    if (!confirm("Lokale Lizenzaktivierung in diesem Browser entfernen?")) return;
    await clearActivation();
    await refreshLicenseStatus();
  }
  return (
    <Page title="Einstellungen">
      <div className="card hero-card">
        <strong>Privacy & Local Data</strong>
        {features.aiAssistEnabled && <p>AI-Assists nutzen externe Anbieter nur nach Privacy Gate. Du siehst genau was übertragen wird und was nicht. ALLE Kernfunktionen funktionieren lokal ohne AI.</p>}
        {features.aiAssistEnabled && features.aiAssistUserToggleEnabled && <label className="settings-switch"><span><strong>AI-Assist aktivieren</strong><small>Ausgeschaltet bleibt die App im normalen Arbeitsfluss local-first und startet keine AI-Assist-Anfragen.</small></span><span className="switch-control"><input type="checkbox" checked={userAiAssistEnabled} onChange={toggleAiAssist} /><span aria-hidden="true" /></span></label>}
        {features.aiAssistEnabled && <p>Credits: {workspace.appSettings[0]?.credits_remaining ?? 0}</p>}
        <button className="button danger" onClick={reset}>Lokale Daten löschen</button>
      </div>
      {gatingConfig.enabled && <div className="card hero-card license-settings-card">
        <strong>Lizenz</strong>
        <p>Status: {licenseAccess.mode === "licensed" ? "Freigeschaltet" : licenseAccess.mode === "grace" ? "Grace Period aktiv" : "Gesperrt"}</p>
        {licenseAccess.activation?.keySuffix && <p>Lizenz: **** {licenseAccess.activation.keySuffix}</p>}
        {licenseAccess.message && <p>{licenseAccess.message}</p>}
        <div className="row-actions">
          <button className="button secondary" onClick={refreshLicenseStatus} disabled={licenseBusy}><RefreshCw /> Lizenz erneut prüfen</button>
          <button className="button danger" onClick={forgetLicense} disabled={licenseBusy}>Aktivierung entfernen</button>
        </div>
      </div>}
      <LegalLinks placement="settings" />
    </Page>
  );
}

function AiAssistButton({ workspace, task, module, useCase, sourceId, label = "AI-Assist starten", variant, onDone }: { workspace: Workspace; task: Parameters<typeof buildPrivacyPayload>[0]["task_type"]; module: string; useCase?: AIUseCase; sourceId?: string; label?: string; variant?: "compact"; onDone: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const requestControllerRef = useRef<AbortController | null>(null);
  const payload = buildPrivacyPayload({ task_type: task, profile: workspace.businessProfile[0], useCase, risks: workspace.riskAssessments, role: workspace.roleCheckRecords.find((item) => item.id === "role_check_current") ?? latestByTimestamp(workspace.roleCheckRecords), transparency: workspace.transparencyChecks.find((item) => item.id === "transparency_check_current") ?? latestByTimestamp(workspace.transparencyChecks), score: latestByTimestamp(workspace.readinessScores) });
  useEffect(() => {
    if (!busy) {
      setProgress(0);
      return;
    }
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      setProgress(Math.min(100, Math.round(((Date.now() - startedAt) / 30000) * 100)));
    }, 250);
    return () => window.clearInterval(interval);
  }, [busy]);
  async function run() {
    const controller = new AbortController();
    requestControllerRef.current = controller;
    setBusy(true);
    try {
      await runAiAssist({ task_type: task, payload: payload.payload, sentFields: payload.sentFields, blockedFields: payload.blockedFields, module, sourceId, signal: controller.signal });
      setOpen(false);
      await onDone();
    } catch (error) {
      if (!controller.signal.aborted) throw error;
    } finally {
      requestControllerRef.current = null;
      setBusy(false);
    }
  }
  function cancel() {
    if (busy) {
      requestControllerRef.current?.abort();
      setOpen(false);
      return;
    }
    setOpen(false);
  }
  return <><button className={`button secondary ${variant === "compact" ? "small ai-chip" : ""}`} onClick={() => setOpen(true)}><Bot size={16} /> {label}</button>{open && <div className="modal"><div className="modal-panel ai-assist-modal-panel"><h2>AI-Assist mit anonymisierten Daten starten?</h2><p>Dieser Vorgang verbraucht 1 AI-Assist. Der Vorgang ist optional.</p><h3>Gesendet wird</h3><ul>{payload.sentFields.map((field) => <li key={field}>{field}</li>)}</ul><h3>Nicht gesendet wird</h3><ul>{payload.blockedFields.map((field) => <li key={field}>{field}</li>)}</ul><div className="modal-actions"><button className="button secondary" onClick={cancel}>Abbrechen</button><button className="button primary" disabled={busy} onClick={run}>{busy ? "Läuft..." : "Mit anonymisierten Daten starten"}</button></div>{busy && <div className="ai-assist-progress-overlay"><AiAssistProgress progress={progress} /></div>}</div></div>}</>;
}

function AiAssistProgress({ progress }: { progress: number }) {
  return (
    <div className="ai-assist-progress" role="status" aria-live="polite">
      <div className="ai-assist-progress-ring">
        <span className="ai-assist-progress-orbit" />
        <strong>{progress}%</strong>
      </div>
      <span>Anfrage wird bearbeitet</span>
    </div>
  );
}

function Label({ label, children }: { label: string; children: ReactNode }) {
  return <label className="field"><span>{label}</span>{children}</label>;
}

function OptionList({ items, groups }: { items: { value: string; label: string }[]; groups?: OptionGroup[] }) {
  if (!groups) return <>{items.map((item) => <option value={item.value} key={item.value}>{item.label}</option>)}</>;
  return (
    <>
      {groupedOptions(items, groups).map((group) => (
        <optgroup label={group.label} key={group.label}>
          {group.options.map((item) => <option value={item.value} key={item.value}>{item.label}</option>)}
        </optgroup>
      ))}
    </>
  );
}

function Status({ state }: { state: string }) {
  return <span className={`status ${state}`}>{modelLabel(state)}</span>;
}

function Traffic({ light }: { light: string }) {
  const label = light === "red" ? "Rot - Externe Prüfung empfohlen" : light === "yellow" ? "Gelb - Aufmerksamkeit sinnvoll" : "Grün - Überschaubar";
  return <span className={`traffic ${light}`}>{label}</span>;
}

function RiskDot({ light }: { light: string }) {
  const label = light === "red" ? "Rot" : light === "yellow" ? "Gelb" : "Grün";
  return <span className="risk-dot-wrap" aria-label={`Risiko ${label}`} title={`Risiko ${label}`}><span className={`risk-dot ${light}`} />{label}</span>;
}

function Ampel({ light }: { light: string }) {
  return (
    <div className="ampel" aria-label={`Ampelstatus ${light}`}>
      <span className={`ampel-light red ${light === "red" ? "active" : ""}`} />
      <span className={`ampel-light yellow ${light === "yellow" ? "active" : ""}`} />
      <span className={`ampel-light green ${light === "green" ? "active" : ""}`} />
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return <table><thead><tr>{headers.map((head) => <th key={head}>{head}</th>)}</tr></thead><tbody>{rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}</tbody></table>;
}
